/**
 * Bitwrench v2 Default Styles
 *
 * CSS-in-JS style definitions providing a complete, Bootstrap-inspired
 * design system. Styles are defined as nested JavaScript objects that
 * bw.css() converts to CSS strings and bw.injectCSS() injects into the DOM.
 *
 * The module exports:
 * - {@link defaultStyles} - All style categories as a structured object
 * - {@link getAllStyles} - Merges all categories into a flat CSS rules object
 * - {@link theme} - Design token configuration (colors, breakpoints, spacing, typography)
 * - {@link generateThemedCSS} - Generate scoped themed CSS from a palette
 * - {@link derivePalette} - Re-export from color-utils for convenience
 *
 * Style categories: root (CSS variables), reset, typography, grid, buttons,
 * cards, forms, navigation, tables, alerts, badges, progress, tabs, listGroups,
 * pagination, breadcrumb, hero, features, enhancedCards, sections, cta,
 * utilities, responsive.
 *
 * @module bitwrench-styles
 * @license BSD-2-Clause
 * @author M A Chatterjee <deftio [at] deftio [dot] com>
 */

import { derivePalette } from './bitwrench-color-utils.js';

// Re-export for use by bitwrench.js
export { derivePalette };

// =========================================================================
// Layout presets
// =========================================================================

/**
 * Base spacing scale (4px unit). Shared design token for consistent spacing
 * across all components. Use scale keys (0-7) in component definitions.
 */
var SPACING_SCALE = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.5rem',    // 24px
  6: '2rem',      // 32px
  7: '3rem'       // 48px
};

let _S=SPACING_SCALE;

export var SPACING_PRESETS = {
  compact:  { btn: _S[1] + ' ' + _S[3],  card: _S[3] + ' ' + _S[4], alert: _S[2] + ' ' + _S[4], cell: _S[2] + ' ' + _S[3], input: _S[1] + ' ' + _S[3] },
  normal:   { btn: _S[2] + ' ' + _S[4],  card: _S[5] + ' ' + _S[5], alert: _S[3] + ' ' + _S[5], cell: _S[3] + ' ' + _S[4], input: _S[2] + ' ' + _S[3] },
  spacious: { btn: _S[3] + ' ' + _S[5],  card: _S[6] + ' ' + _S[6], alert: _S[4] + ' ' + _S[5], cell: _S[4] + ' ' + _S[5], input: _S[3] + ' ' + _S[4] }
};

export var RADIUS_PRESETS = {
  none: { btn: '0', card: '0', badge: '0', alert: '0', input: '0' },
  sm:   { btn: '4px', card: '4px', badge: '.25rem', alert: '4px', input: '4px' },
  md:   { btn: '6px', card: '8px', badge: '.375rem', alert: '8px', input: '6px' },
  lg:   { btn: '10px', card: '12px', badge: '.5rem', alert: '12px', input: '10px' },
  pill: { btn: '50rem', card: '1rem', badge: '50rem', alert: '1rem', input: '50rem' }
};

// ---- Typography scale presets ----

export var TYPE_RATIO_PRESETS = {
  tight:    1.125,
  normal:   1.200,
  relaxed:  1.250,
  dramatic: 1.333
};

/**
 * Generate a modular type scale from a base size and ratio.
 * @param {number} base - Base font size in px (default 16)
 * @param {number} ratio - Scale ratio (default 1.200)
 * @returns {Object} { xs, sm, base, lg, xl, '2xl', '3xl', '4xl' } in px
 */
export function generateTypeScale(base, ratio) {
  if (!base) base = 16;
  if (!ratio) ratio = 1.200;
  return {
    xs:   Math.round(base / (ratio * ratio)),
    sm:   Math.round(base / ratio),
    base: base,
    lg:   Math.round(base * ratio),
    xl:   Math.round(base * ratio * ratio),
    '2xl': Math.round(base * Math.pow(ratio, 3)),
    '3xl': Math.round(base * Math.pow(ratio, 4)),
    '4xl': Math.round(base * Math.pow(ratio, 5))
  };
}

// ---- Elevation (shadow depth) presets ----

export var ELEVATION_PRESETS = {
  flat: {
    sm:  'none',
    md:  'none',
    lg:  'none',
    xl:  'none'
  },
  sm: {
    sm:  '0 1px 2px rgba(0,0,0,0.05)',
    md:  '0 1px 3px rgba(0,0,0,0.08)',
    lg:  '0 2px 6px rgba(0,0,0,0.10)',
    xl:  '0 4px 12px rgba(0,0,0,0.12)'
  },
  md: {
    sm:  '0 1px 3px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)',
    md:  '0 4px 6px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)',
    lg:  '0 10px 15px rgba(0,0,0,0.12), 0 4px 6px rgba(0,0,0,0.08)',
    xl:  '0 20px 25px rgba(0,0,0,0.15), 0 8px 10px rgba(0,0,0,0.10)'
  },
  lg: {
    sm:  '0 2px 4px rgba(0,0,0,0.10)',
    md:  '0 4px 12px rgba(0,0,0,0.16)',
    lg:  '0 8px 24px rgba(0,0,0,0.22)',
    xl:  '0 16px 48px rgba(0,0,0,0.28)'
  }
};

// ---- Motion (transition) presets ----

export var MOTION_PRESETS = {
  reduced: {
    fast:    '0ms',
    normal:  '0ms',
    slow:    '0ms',
    easing:  'linear'
  },
  standard: {
    fast:    '100ms',
    normal:  '200ms',
    slow:    '300ms',
    easing:  'ease-out'
  },
  expressive: {
    fast:    '150ms',
    normal:  '300ms',
    slow:    '500ms',
    easing:  'cubic-bezier(0.34, 1.56, 0.64, 1)'
  }
};

/**
 * Default palette config — matches existing hardcoded colors
 */
export var DEFAULT_PALETTE_CONFIG = {
  primary: '#006666',
  secondary: '#6c757d',
  tertiary: '#006666',
  success: '#198754',
  danger: '#dc3545',
  warning: '#b38600',
  info: '#0891b2',
  light: '#f8f9fa',
  dark: '#212529'
};

/**
 * Built-in theme presets — named color combinations
 * Each preset provides primary, secondary, and tertiary seed colors.
 */
export var THEME_PRESETS = Object.fromEntries([
  ['teal','#006666','#6c757d','#006666'],['ocean','#0077b6','#90e0ef','#00b4d8'],
  ['sunset','#e76f51','#264653','#e9c46a'],['forest','#2d6a4f','#95d5b2','#52b788'],
  ['slate','#343a40','#adb5bd','#6c757d'],['rose','#e11d48','#fda4af','#fb7185'],
  ['indigo','#4f46e5','#a5b4fc','#818cf8'],['amber','#d97706','#fbbf24','#f59e0b'],
  ['emerald','#059669','#6ee7b7','#34d399'],['nord','#5e81ac','#88c0d0','#81a1c1'],
  ['coral','#ef6461','#4a7c7e','#e8a87c'],['midnight','#1e3a5f','#7c8db5','#3d5a80']
].map(function(e) { return [e[0], {primary:e[1],secondary:e[2],tertiary:e[3]}]; }));

/**
 * Resolve layout config to spacing, radius, typeScale, elevation, and motion objects.
 * @param {Object} config - { spacing, radius, fontSize, typeRatio, elevation, motion }
 * @returns {Object} { spacing, radius, fontSize, typeScale, elevation, motion }
 */
export function resolveLayout(config) {
  var sp = (config && config.spacing) || 'normal';
  var rd = (config && config.radius) || 'md';
  var fs = (config && config.fontSize) || 1.0;

  // typeRatio: accept preset name or number
  var tr = (config && config.typeRatio) || 'normal';
  var ratioNum = typeof tr === 'string' ? (TYPE_RATIO_PRESETS[tr] || TYPE_RATIO_PRESETS.normal) : tr;

  // elevation: accept preset name or object
  var el = (config && config.elevation) || 'md';

  // motion: accept preset name or object
  var mo = (config && config.motion) || 'standard';

  return {
    spacing: typeof sp === 'string' ? (SPACING_PRESETS[sp] || SPACING_PRESETS.normal) : sp,
    radius: typeof rd === 'string' ? (RADIUS_PRESETS[rd] || RADIUS_PRESETS.md) : rd,
    fontSize: fs,
    typeScale: generateTypeScale(16, ratioNum),
    elevation: typeof el === 'string' ? (ELEVATION_PRESETS[el] || ELEVATION_PRESETS.md) : el,
    motion: typeof mo === 'string' ? (MOTION_PRESETS[mo] || MOTION_PRESETS.standard) : mo
  };
}

// =========================================================================
// Scoping helper
// =========================================================================

/**
 * Prefix a CSS selector with a scope class name.
 * @param {string} name - Scope class (e.g. 'ocean'). Empty = no scoping.
 * @param {string} sel - CSS selector(s)
 * @returns {string} Scoped selector
 */
function scopeSelector(name, sel) {
  if (!name) return sel;
  /* c8 ignore next -- scopeSelector never receives comma-separated selectors from internal callers */
  if (sel.includes(',')) return sel.split(',').map(function(s) { return '.' + name + ' ' + s.trim(); }).join(', ');
  return '.' + name + ' ' + sel;
}
var _sx=scopeSelector;
function _tr(p,m,s){var d=m[s||'fast'];return p.split(',').map(function(x){return x.trim()+' '+d+' '+m.easing;}).join(', ');}
function _fr(f,sz){return '0 0 0 '+(sz||'0.25rem')+' '+f;}
function _sf(p){return p.surface||'#fff';}

// =========================================================================
// Themed CSS generators
// =========================================================================

function generateTypographyThemed(scope, palette, layout) {
  var mot = layout.motion;
  var rules = {};
  rules[_sx(scope, 'a')] = {
    'color': palette.primary.base,
    'text-decoration': 'none',
    'transition': _tr('color', mot)
  };
  rules[_sx(scope, 'a:hover')] = {
    'color': palette.tertiary.hover,
    'text-decoration': 'underline'
  };
  return rules;
}

function generateButtons(scope, palette, layout) {
  var rules = {};
  var sp = layout.spacing;
  var rd = layout.radius;

  // Base button (only when scoped — unscoped uses defaultStyles)
  rules[_sx(scope, '.bw_bccl_btn')] = {
    'padding': sp.btn,
    'border-radius': rd.btn
  };
  rules[_sx(scope, '.bw_bccl_btn:focus-visible')] = {
    'outline': '2px solid currentColor',
    'outline-offset': '2px',
    'box-shadow': _fr(palette.primary.focus, '3px')
  };

  // Variant colors handled by palette class on component root

  // Size variants (structural, reuse layout radius)
  rules[_sx(scope, '.bw_bccl_btn_lg')] = {
    'padding': '0.625rem 1.5rem',
    'font-size': '1rem',
    'border-radius': rd.btn === '50rem' ? '50rem' : (parseInt(rd.btn) + 2) + 'px'
  };
  rules[_sx(scope, '.bw_bccl_btn_sm')] = {
    'padding': '0.25rem 0.75rem',
    'font-size': '0.8125rem',
    'border-radius': rd.btn === '50rem' ? '50rem' : (Math.max(parseInt(rd.btn) - 1, 0)) + 'px'
  };

  return rules;
}

function generateAlerts(scope, palette, layout) {
  var rules = {};
  var sp = layout.spacing;
  var rd = layout.radius;

  rules[_sx(scope, '.bw_bccl_alert')] = {
    'padding': sp.alert,
    'border-radius': rd.alert
  };

  // Variant colors handled by palette class on component root

  return rules;
}

// generateBadges: removed — palette class on root handles variants

function generateCards(scope, palette, layout) {
  var rules = {};
  var sp = layout.spacing;
  var rd = layout.radius;

  var elev = layout.elevation;
  var motion = layout.motion;
  rules[_sx(scope, '.bw_bccl_card')] = {
    'background-color': _sf(palette),
    'border': '1px solid ' + palette.light.border,
    'border-radius': rd.card,
    'box-shadow': elev.sm,
    'transition': _tr('box-shadow,transform', motion, 'normal')
  };
  rules[_sx(scope, '.bw_bccl_card:hover')] = {
    'box-shadow': elev.md
  };
  rules[_sx(scope, '.bw_bccl_card_hoverable')] = {
    'transition': _tr('box-shadow,transform', motion, 'slow')
  };
  rules[_sx(scope, '.bw_bccl_card_hoverable:hover')] = {
    'box-shadow': elev.lg
  };
  rules[_sx(scope, '.bw_bccl_card_body')] = {
    'padding': sp.card
  };
  rules[_sx(scope, '.bw_bccl_card_header')] = {
    'padding': sp.card.split(' ').map(function(v) { return (parseFloat(v) * 0.7).toFixed(3).replace(/\.?0+$/, '') + 'rem'; }).join(' '),
    'background-color': palette.surfaceAlt,
    'border-bottom': '1px solid ' + palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_card_footer')] = {
    'background-color': palette.surfaceAlt,
    'border-top': '1px solid ' + palette.light.border,
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_bccl_card_title')] = {
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_bccl_card_subtitle')] = {
    'color': palette.secondary.base
  };

  // Card variant accent handled by palette class on component root

  return rules;
}

function generateForms(scope, palette, layout) {
  var rules = {};
  var sp = layout.spacing;
  var rd = layout.radius;

  rules[_sx(scope, '.bw_bccl_form_control')] = {
    'padding': sp.input,
    'border-radius': rd.input,
    'color': palette.dark.base,
    'background-color': _sf(palette),
    'border-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_form_control:focus')] = {
    'border-color': palette.primary.border,
    'outline': '2px solid ' + palette.primary.base,
    'outline-offset': '-1px',
    'box-shadow': _fr(palette.primary.focus)
  };
  rules[_sx(scope, '.bw_bccl_form_control::placeholder')] = {
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_bccl_form_label')] = {
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_bccl_form_text')] = {
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_bccl_form_check_input:checked')] = {
    'background-color': palette.primary.base,
    'border-color': palette.primary.base
  };
  rules[_sx(scope, '.bw_bccl_form_check_input:focus')] = {
    'box-shadow': _fr(palette.primary.focus)
  };
  // Validation states
  rules[_sx(scope, '.bw_bccl_form_control.bw_is_valid')] = { 'border-color': palette.success.base };
  rules[_sx(scope, '.bw_bccl_form_control.bw_is_valid:focus')] = {
    'border-color': palette.success.base,
    'box-shadow': _fr(palette.success.focus, '0.2rem')
  };
  rules[_sx(scope, '.bw_bccl_form_control.bw_is_invalid')] = { 'border-color': palette.danger.base };
  rules[_sx(scope, '.bw_bccl_form_control.bw_is_invalid:focus')] = {
    'border-color': palette.danger.base,
    'box-shadow': _fr(palette.danger.focus, '0.2rem')
  };
  // Form select
  rules[_sx(scope, '.bw_bccl_form_select')] = {
    'padding': sp.input,
    'border-radius': rd.input,
    'color': palette.dark.base,
    'background-color': _sf(palette),
    'border-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_form_select:focus')] = {
    'border-color': palette.primary.border,
    'box-shadow': _fr(palette.primary.focus)
  };

  return rules;
}

function generateNavigation(scope, palette, layout) {
  var rules = {};
  rules[_sx(scope, '.bw_bccl_navbar')] = {
    'background-color': palette.surfaceAlt,
    'border-bottom-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_navbar_brand')] = {
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_bccl_navbar_nav .bw_nav_link')] = {
    'color': palette.secondary.base,
    'border-radius': layout.radius.btn,
    'transition': _tr('color,background-color', layout.motion)
  };
  rules[_sx(scope, '.bw_bccl_navbar_nav .bw_nav_link:hover')] = {
    'color': palette.tertiary.base,
    'background-color': palette.surfaceAlt
  };
  rules[_sx(scope, '.bw_bccl_navbar_nav .bw_nav_link.active')] = {
    'color': palette.primary.base,
    'background-color': palette.primary.focus,
    'font-weight': '600'
  };
  rules[_sx(scope, '.bw_bccl_navbar_dark')] = {
    'background-color': palette.dark.base,
    'border-bottom-color': palette.dark.hover
  };
  rules[_sx(scope, '.bw_bccl_navbar_dark .bw_bccl_navbar_brand')] = {
    'color': palette.light.base
  };
  rules[_sx(scope, '.bw_bccl_navbar_dark .bw_nav_link')] = {
    'color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_navbar_dark .bw_nav_link:hover')] = {
    'color': palette.light.base
  };
  rules[_sx(scope, '.bw_bccl_navbar_dark .bw_nav_link.active')] = {
    'color': palette.light.base,
    'font-weight': '600'
  };
  rules[_sx(scope, '.bw_nav_pills .bw_nav_link.active')] = {
    'color': palette.primary.textOn,
    'background-color': palette.primary.base
  };
  return rules;
}

function generateTables(scope, palette, layout) {
  var rules = {};
  var sp = layout.spacing;

  rules[_sx(scope, '.bw_bccl_table')] = {
    'color': palette.dark.base,
    'border-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_table > :not(caption) > * > *')] = {
    'padding': sp.cell,
    'border-bottom-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_table > thead > tr > *')] = {
    'color': palette.secondary.base,
    'border-bottom-color': palette.light.border,
    'background-color': palette.surfaceAlt
  };
  rules[_sx(scope, '.bw_bccl_table_striped > tbody > tr:nth-of-type(odd) > *')] = {
    'background-color': palette.surfaceAlt
  };
  rules[_sx(scope, '.bw_bccl_table_hover > tbody > tr:hover > *')] = {
    'background-color': palette.primary.focus
  };
  rules[_sx(scope, '.bw_bccl_table_selectable > tbody > tr')] = {
    'cursor': 'pointer'
  };
  rules[_sx(scope, '.bw_bccl_table > tbody > tr.bw_bccl_table_row_selected > *')] = {
    'background-color': palette.primary.light
  };
  rules[_sx(scope, '.bw_bccl_table_bordered')] = {
    'border-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_table caption')] = {
    'color': palette.secondary.base
  };

  return rules;
}

function generateTabs(scope, palette, layout) {
  var rules = {}, mo = layout.motion;
  rules[_sx(scope, '.bw_nav_tabs')] = {
    'border-bottom-color': palette.light.border
  };
  rules[_sx(scope, '.bw_nav_link')] = {
    'color': palette.secondary.base,
    'transition': _tr('color,border-color,background-color', mo)
  };
  rules[_sx(scope, '.bw_nav_tabs .bw_nav_link:hover')] = {
    'color': palette.tertiary.base,
    'background-color': palette.surfaceAlt,
    'border-bottom-color': palette.light.border
  };
  rules[_sx(scope, '.bw_nav_tabs .bw_nav_link.active')] = {
    'color': palette.primary.base,
    'background-color': palette.primary.focus,
    'border-bottom': '2px solid ' + palette.primary.base
  };
  return rules;
}

function generateListGroups(scope, palette, layout) {
  var rules = {};
  var sp = layout.spacing;
  var mo = layout.motion;

  rules[_sx(scope, '.bw_list_group_item')] = {
    'padding': sp.cell,
    'color': palette.dark.base,
    'background-color': _sf(palette),
    'border-color': palette.light.border,
    'transition': _tr('color,background-color', mo)
  };
  rules[_sx(scope, 'a.bw_list_group_item:hover')] = {
    'background-color': palette.surfaceAlt,
    'color': palette.tertiary.base
  };
  rules[_sx(scope, '.bw_list_group_item.active')] = {
    'color': palette.primary.textOn,
    'background-color': palette.primary.base,
    'border-color': palette.primary.base
  };
  rules[_sx(scope, '.bw_list_group_item.disabled')] = {
    'color': palette.secondary.base,
    'background-color': _sf(palette)
  };

  return rules;
}

function generatePagination(scope, palette, layout) {
  var rules = {}, mo = layout.motion, rd = layout.radius;
  rules[_sx(scope, '.bw_page_item:first-child .bw_page_link')] = {
    'border-top-left-radius': rd.btn,
    'border-bottom-left-radius': rd.btn
  };
  rules[_sx(scope, '.bw_page_item:last-child .bw_page_link')] = {
    'border-top-right-radius': rd.btn,
    'border-bottom-right-radius': rd.btn
  };
  rules[_sx(scope, '.bw_page_link')] = {
    'color': palette.primary.base,
    'background-color': _sf(palette),
    'border-color': palette.light.border,
    'transition': _tr('color,background-color', mo)
  };
  rules[_sx(scope, '.bw_page_link:hover')] = {
    'color': palette.primary.hover,
    'background-color': palette.surfaceAlt,
    'border-color': palette.light.border
  };
  rules[_sx(scope, '.bw_page_link:focus')] = {
    'outline': '2px solid ' + palette.primary.base,
    'outline-offset': '-2px'
  };
  rules[_sx(scope, '.bw_page_item.bw_active .bw_page_link')] = {
    'color': palette.primary.textOn,
    'background-color': palette.primary.base,
    'border-color': palette.primary.base
  };
  rules[_sx(scope, '.bw_page_item.bw_disabled .bw_page_link')] = {
    'color': palette.secondary.base,
    'background-color': _sf(palette),
    'border-color': palette.light.border
  };
  return rules;
}

function generateProgress(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_bccl_progress')] = {
    'background-color': palette.surfaceAlt,
    'border-radius': rd.badge,
    'box-shadow': 'inset 0 1px 2px rgba(0,0,0,.1)'
  };
  rules[_sx(scope, '.bw_bccl_progress_bar')] = {
    'color': palette.primary.textOn,
    'background-color': palette.primary.base,
    'border-radius': 'inherit',
    'box-shadow': 'inset 0 -1px 0 rgba(0,0,0,.15)'
  };
  // Variant progress bar colors handled by palette class
  return rules;
}

// generateHero: removed — palette class with .bw_bccl_hero override handles variants

// generateUtilityColors: removed — palette classes replace utility colors

function generateResetThemed(scope, palette) {
  var rules = {};
  var bg = palette.background || '#f5f5f5';
  var baseReset = {
    'color': palette.dark.base,
    'background-color': bg
  };
  rules[_sx(scope, 'body')] = baseReset;
  // Also apply to the scope element itself so themes work on any container, not just body
  if (scope) {
    rules['.' + scope] = baseReset;
  }
  return rules;
}

function generateProseThemed(scope, palette) {
  var rules = {};
  // Bare hr draws a rule now (see structuralRules.base), so it needs the
  // palette colour too -- otherwise it falls back to the UA's grey currentColor.
  rules[_sx(scope, 'hr, .bw_hr')] = { 'border-top-color': palette.light.border };
  rules[_sx(scope, '.bw_quote')] = {
    'border-left-color': palette.primary.base,
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_code')] = {
    'background-color': palette.surfaceAlt,
    'color': palette.danger.darkText
  };
  return rules;
}

function generateBreadcrumbThemed(scope, palette, layout) {
  var rules = {}, mo = layout.motion;
  rules[_sx(scope, '.bw_bccl_breadcrumb')] = {
    'background-color': palette.surfaceAlt,
    'padding': '0.625rem 1rem',
    'border-radius': layout.radius.btn
  };
  rules[_sx(scope, '.bw_bccl_breadcrumb_item + .bw_bccl_breadcrumb_item::before')] = {
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_bccl_breadcrumb_item a')] = {
    'color': palette.tertiary.base,
    'transition': _tr('color', mo)
  };
  rules[_sx(scope, '.bw_bccl_breadcrumb_item a:hover')] = {
    'color': palette.tertiary.hover,
    'text-decoration': 'underline'
  };
  rules[_sx(scope, '.bw_bccl_breadcrumb_item.active')] = {
    'color': palette.dark.base
  };
  return rules;
}

// generateSpinnerThemed: removed — palette class on root handles variants

function generateCloseButtonThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_close')] = {
    'color': palette.dark.base,
    'opacity': '0.5'
  };
  rules[_sx(scope, '.bw_close:focus')] = {
    'box-shadow': _fr(palette.primary.focus)
  };
  return rules;
}

function generateSectionsThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_section_subtitle')] = {
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_feature_description')] = {
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_cta_description')] = {
    'color': palette.secondary.base
  };
  return rules;
}

function generateAccordionThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_bccl_accordion_item')] = {
    'background-color': _sf(palette),
    'border-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_accordion_item:first-child')] = {
    'border-top-left-radius': rd.card,
    'border-top-right-radius': rd.card
  };
  rules[_sx(scope, '.bw_bccl_accordion_item:last-child')] = {
    'border-bottom-left-radius': rd.card,
    'border-bottom-right-radius': rd.card
  };
  rules[_sx(scope, '.bw_bccl_accordion_button')] = {
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_bccl_accordion_button:not(.bw_collapsed)')] = {
    'color': palette.primary.darkText,
    'background-color': palette.primary.light,
    'border-left': '3px solid ' + palette.primary.base
  };
  rules[_sx(scope, '.bw_bccl_accordion_button:hover')] = {
    'background-color': palette.surfaceAlt
  };
  rules[_sx(scope, '.bw_bccl_accordion_button:not(.bw_collapsed):hover')] = {
    'background-color': palette.primary.base,
    'color': palette.primary.textOn
  };
  rules[_sx(scope, '.bw_bccl_accordion_button:focus-visible')] = {
    'box-shadow': _fr(palette.primary.focus, '0.2rem')
  };
  rules[_sx(scope, '.bw_bccl_accordion_body')] = {
    'border-top': '1px solid ' + palette.light.border,
    'background-color': palette.surfaceAlt
  };
  return rules;
}

function generateCarouselThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_bccl_carousel')] = {
    'background-color': palette.surfaceAlt
  };
  rules[_sx(scope, '.bw_bccl_carousel_indicator.active')] = {
    'background-color': palette.primary.base
  };
  rules[_sx(scope, '.bw_bccl_carousel_control')] = {
    'background-color': palette.dark.base,
    'color': palette.dark.textOn,
    'transition': 'background-color 0.15s ease-out'
  };
  rules[_sx(scope, '.bw_bccl_carousel_control:hover')] = {
    'background-color': palette.dark.hover
  };
  rules[_sx(scope, '.bw_bccl_carousel_caption')] = {
    'background': 'linear-gradient(transparent, ' + palette.dark.base + ')',
    'color': palette.dark.textOn
  };
  return rules;
}

function generateModalThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_bccl_modal_content')] = {
    'background-color': _sf(palette),
    'border-color': palette.light.border,
    'border-radius': rd.card,
    'box-shadow': layout.elevation.lg
  };
  rules[_sx(scope, '.bw_bccl_modal_header')] = {
    'border-bottom-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_modal_footer')] = {
    'border-top-color': palette.light.border
  };
  rules[_sx(scope, '.bw_bccl_modal_title')] = {
    'color': palette.dark.base
  };
  return rules;
}

function generateToastThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_bccl_toast')] = {
    'background-color': _sf(palette),
    'border-color': palette.light.border,
    'border-radius': rd.card,
    'box-shadow': layout.elevation.lg
  };
  rules[_sx(scope, '.bw_bccl_toast_header')] = {
    'border-bottom-color': palette.light.border
  };
  // Variant toast borders handled by palette class
  return rules;
}

function generateDropdownThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_bccl_dropdown_menu')] = {
    'background-color': _sf(palette),
    'border-color': palette.light.border,
    'border-radius': rd.card,
    'box-shadow': layout.elevation.md
  };
  rules[_sx(scope, '.bw_bccl_dropdown_item')] = {
    'color': palette.dark.base,
    'transition': _tr('background-color', layout.motion)
  };
  rules[_sx(scope, '.bw_bccl_dropdown_item:hover')] = {
    'color': palette.dark.hover,
    'background-color': palette.surfaceAlt
  };
  rules[_sx(scope, '.bw_bccl_dropdown_item.disabled')] = {
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_bccl_dropdown_divider')] = {
    'border-top-color': palette.light.border
  };
  return rules;
}

function generateSwitchThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_bccl_form_switch .bw_switch_input')] = {
    'background-color': palette.secondary.base,
    'border-color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_bccl_form_switch .bw_switch_input:checked')] = {
    'background-color': palette.primary.base,
    'border-color': palette.primary.base
  };
  rules[_sx(scope, '.bw_bccl_form_switch .bw_switch_input:focus')] = {
    'box-shadow': _fr(palette.primary.focus)
  };
  rules[_sx(scope, '.bw_bccl_form_switch .bw_switch_input:focus-visible')] = {
    'box-shadow': _fr(palette.primary.focus),
    'outline': 'none'
  };
  return rules;
}

function generateSkeletonThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_skeleton')] = {
    'background': 'linear-gradient(90deg, ' + palette.light.border + ' 25%, ' + palette.surfaceAlt + ' 37%, ' + palette.light.border + ' 63%)'
  };
  return rules;
}

// generateAvatarThemed: removed — palette class on root handles variants

function generateStatCardThemed(scope, palette, layout) {
  var rules = {}, mo = layout.motion, el = layout.elevation, rd = layout.radius;
  rules[_sx(scope, '.bw_stat_card')] = {
    'background-color': _sf(palette),
    'color': palette.dark.base,
    'border': '1px solid ' + palette.light.border,
    'border-radius': rd.card,
    'box-shadow': el.sm,
    'transition': _tr('box-shadow,transform', mo)
  };
  rules[_sx(scope, '.bw_stat_card:hover')] = { 'box-shadow': el.md };
  // Variant border colors handled by palette class
  rules[_sx(scope, '.bw_stat_change_up')] = { 'color': palette.success.base };
  rules[_sx(scope, '.bw_stat_change_down')] = { 'color': palette.danger.base };
  return rules;
}

function generateTimelineThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_bccl_timeline::before')] = { 'background-color': palette.light.border };
  // Variant marker colors handled by palette class
  rules[_sx(scope, '.bw_bccl_timeline_date')] = { 'color': palette.secondary.base };
  return rules;
}

function generateStepperThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_step_indicator')] = {
    'background-color': palette.surfaceAlt,
    'border': '2px solid ' + palette.light.border,
    'color': palette.secondary.base
  };
  rules[_sx(scope, '.bw_step + .bw_step::before')] = { 'background-color': palette.light.border };
  rules[_sx(scope, '.bw_step_active .bw_step_indicator')] = {
    'background-color': palette.primary.base,
    'color': palette.primary.textOn
  };
  rules[_sx(scope, '.bw_step_active .bw_step_label')] = {
    'color': palette.dark.base,
    'font-weight': '600'
  };
  rules[_sx(scope, '.bw_step_completed .bw_step_indicator')] = {
    'background-color': palette.tertiary.base,
    'color': palette.tertiary.textOn
  };
  rules[_sx(scope, '.bw_step_completed .bw_step_label')] = { 'color': palette.tertiary.base };
  rules[_sx(scope, '.bw_step_completed + .bw_step::before')] = { 'background-color': palette.tertiary.base };
  return rules;
}

function generateChipInputThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_chip_input')] = {
    'border-color': palette.light.border,
    'background-color': _sf(palette),
    'color': palette.dark.base,
    'border-radius': rd.input
  };
  rules[_sx(scope, '.bw_chip_input:focus-within')] = {
    'border-color': palette.primary.base,
    'box-shadow': _fr(palette.primary.focus, '0.2rem')
  };
  rules[_sx(scope, '.bw_chip')] = {
    'background-color': palette.surfaceAlt,
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_chip_remove:hover')] = {
    'color': palette.danger.base,
    'background-color': palette.danger.light
  };
  return rules;
}

function generateFileUploadThemed(scope, palette, layout) {
  var rules = {}, mo = layout.motion;
  rules[_sx(scope, '.bw_file_upload')] = {
    'border-color': palette.light.border,
    'background-color': palette.surfaceAlt,
    'transition': _tr('border-color,background-color', mo)
  };
  rules[_sx(scope, '.bw_file_upload:hover')] = {
    'border-color': palette.primary.base,
    'background-color': palette.primary.light
  };
  rules[_sx(scope, '.bw_file_upload:focus')] = {
    'outline': '2px solid ' + palette.primary.base,
    'outline-offset': '2px'
  };
  rules[_sx(scope, '.bw_file_upload.bw_file_upload_active')] = {
    'border-color': palette.primary.base,
    'background-color': palette.primary.light,
    'border-style': 'solid'
  };
  return rules;
}

function generateRangeThemed(scope, palette) {
  var rules = {};
  rules[_sx(scope, '.bw_range')] = { 'background-color': palette.light.border };
  rules[_sx(scope, '.bw_range::-webkit-slider-thumb')] = {
    'background-color': palette.primary.base,
    'border-color': _sf(palette),
    'box-shadow': '0 1px 3px rgba(0,0,0,0.2)',
    'transition': 'background-color 0.15s ease-out, transform 0.15s ease-out'
  };
  rules[_sx(scope, '.bw_range::-moz-range-thumb')] = {
    'background-color': palette.primary.base,
    'border-color': _sf(palette),
    'box-shadow': '0 1px 3px rgba(0,0,0,0.2)'
  };
  return rules;
}

function generateTooltipThemed(scope, palette, layout) {
  var rules = {}, sp = layout.spacing, rd = layout.radius, el = layout.elevation, mo = layout.motion;
  rules[_sx(scope, '.bw_bccl_tooltip')] = {
    'background-color': palette.dark.base, 'color': palette.dark.textOn,
    'padding': sp.input, 'border-radius': rd.badge, 'box-shadow': el.md,
    'transition': _tr('opacity,transform', mo)
  };
  return rules;
}

function generatePopoverThemed(scope, palette, layout) {
  var rules = {}, sp = layout.spacing, rd = layout.radius, el = layout.elevation, mo = layout.motion;
  rules[_sx(scope, '.bw_popover')] = {
    'background-color': _sf(palette), 'color': palette.dark.base,
    'border': '1px solid ' + palette.light.border, 'border-radius': rd.card, 'box-shadow': el.lg,
    'transition': _tr('opacity,transform', mo)
  };
  rules[_sx(scope, '.bw_popover_header')] = {
    'background-color': palette.surfaceAlt, 'border-bottom': '1px solid ' + palette.light.border,
    'padding': sp.input
  };
  rules[_sx(scope, '.bw_popover_body')] = { 'padding': sp.card };
  return rules;
}

function generateSearchThemed(scope, palette, layout) {
  var rules = {}, mo = layout.motion;
  rules[_sx(scope, '.bw_search_input')] = {
    'background-color': _sf(palette),
    'color': palette.dark.base
  };
  rules[_sx(scope, '.bw_search_clear')] = {
    'transition': _tr('color,background-color', mo)
  };
  rules[_sx(scope, '.bw_search_clear:hover')] = { 'color': palette.dark.base };
  return rules;
}

function generateCodeDemoThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_code_demo')] = {
    'background-color': _sf(palette),
    'color': palette.dark.base,
    'border-radius': rd.card
  };
  rules[_sx(scope, '.bw_code_copy_btn_copied')] = {
    'background': palette.success.base,
    'color': palette.success.textOn,
    'border-color': palette.success.base
  };
  rules[_sx(scope, '.bw_copy_btn:hover')] = {
    'background': 'rgba(255,255,255,0.2)',
    'color': '#fff'
  };
  return rules;
}

function generateNavPillsThemed(scope, palette, layout) {
  var rules = {};
  var rd = layout.radius;
  rules[_sx(scope, '.bw_nav_pills .bw_nav_link')] = { 'border-radius': rd.btn };
  return rules;
}

// =========================================================================
// Palette classes — single-class theming for ALL components
// =========================================================================

/**
 * Generate palette root classes. Each palette color (primary, secondary, etc.)
 * gets ONE class that sets bg, text, and border. Components add this single
 * class to their root element for variant styling. Component-specific overrides
 * (e.g. alerts use light bg, toasts use border-left accent) are included.
 *
 * @param {string} scope - CSS scope class ('' for global)
 * @param {Object} palette - From derivePalette()
 * @returns {Object} CSS rules object
 */
function generatePaletteClasses(scope, palette) {
  var rules = {};
  var keys = Object.keys(palette);
  keys.forEach(function(k) {
    if (typeof palette[k] !== 'object') return;
    var s = palette[k];

    // --- Root palette class: sets default bg/color/border ---
    rules[_sx(scope, '.bw_' + k)] = {
      'background-color': s.base,
      'color': s.textOn,
      'border-color': s.base
    };

    // --- Pseudo-states (shared across all components) ---
    rules[_sx(scope, '.bw_' + k + ':hover')] = {
      'background-color': s.hover,
      'border-color': s.active
    };
    rules[_sx(scope, '.bw_' + k + ':active')] = {
      'background-color': s.active
    };
    rules[_sx(scope, '.bw_' + k + ':focus-visible')] = {
      'box-shadow': '0 0 0 3px ' + s.focus,
      'outline': 'none'
    };

    // --- Component-specific overrides ---

    // Alerts: light bg, dark text, subtle border
    rules[_sx(scope, '.bw_bccl_alert.bw_' + k)] = {
      'background-color': s.light,
      'color': s.darkText,
      'border-color': s.border
    };

    // Toast: inherit bg, left border accent
    rules[_sx(scope, '.bw_bccl_toast.bw_' + k)] = {
      'background-color': 'inherit',
      'color': 'inherit',
      'border-left': '4px solid ' + s.base
    };

    // Stat card: inherit bg, left border accent
    rules[_sx(scope, '.bw_stat_card.bw_' + k)] = {
      'background-color': 'inherit',
      'color': 'inherit',
      'border-left-color': s.base
    };

    // Card accent: left border accent, inherit bg
    rules[_sx(scope, '.bw_bccl_card.bw_' + k)] = {
      'background-color': 'inherit',
      'color': 'inherit',
      'border-left': '4px solid ' + s.base
    };

    // Timeline marker: colored dot
    rules[_sx(scope, '.bw_bccl_timeline_marker.bw_' + k)] = {
      'box-shadow': '0 0 0 2px ' + s.base
    };

    // Spinner: set color, re-apply border pattern so the root palette class
    // border-color doesn't fill in the transparent gap that makes it spin.
    // Also neutralize hover/active which would override border-right-color.
    rules[_sx(scope, '.bw_spinner_border.bw_' + k)] = {
      'background-color': 'transparent',
      'color': s.base,
      'border-color': s.base,
      'border-right-color': 'transparent'
    };
    rules[_sx(scope, '.bw_spinner_border.bw_' + k + ':hover')] = {
      'background-color': 'transparent',
      'border-color': s.base,
      'border-right-color': 'transparent'
    };
    rules[_sx(scope, '.bw_spinner_grow.bw_' + k)] = {
      'background-color': s.base,
      'color': s.base
    };

    // Outline button: transparent bg, colored border+text, solid on hover
    rules[_sx(scope, '.bw_bccl_btn_outline.bw_' + k)] = {
      'background-color': 'transparent',
      'color': s.base,
      'border-color': s.base
    };
    rules[_sx(scope, '.bw_bccl_btn_outline.bw_' + k + ':hover')] = {
      'background-color': s.base,
      'color': s.textOn
    };

    // Hero: gradient background
    rules[_sx(scope, '.bw_bccl_hero.bw_' + k)] = {
      'background': 'linear-gradient(135deg, ' + s.base + ' 0%, ' + s.hover + ' 100%)',
      'color': s.textOn
    };

    // Progress bar: contrasting text on colored bg
    rules[_sx(scope, '.bw_bccl_progress_bar.bw_' + k)] = {
      'color': s.textOn
    };

    // Background utility: .bw_bg_primary, .bw_bg_secondary, etc.
    rules[_sx(scope, '.bw_bg_' + k)] = {
      'background-color': s.base,
      'color': s.textOn
    };

    // Text color utility: .bw_text_primary, .bw_text_secondary, etc.
    rules[_sx(scope, '.bw_text_' + k)] = {
      'color': s.base
    };
  });

  // Text muted — uses palette secondary for theme-aware muted text
  rules[_sx(scope, '.bw_text_muted')] = { 'color': palette.secondary.base };

  // Common bg/text utilities — derive from palette for theme awareness
  rules[_sx(scope, '.bw_bg_dark')] = { 'background-color': palette.dark.base, 'color': palette.dark.textOn };
  rules[_sx(scope, '.bw_bg_light')] = { 'background-color': palette.light.base, 'color': palette.light.textOn };
  rules[_sx(scope, '.bw_text_light')] = { 'color': palette.light.base };
  rules[_sx(scope, '.bw_text_dark')] = { 'color': palette.dark.base };

  return rules;
}

/**
 * Generate all themed CSS rules from a palette and layout.
 * Returns a flat CSS rules object (selector → declarations).
 *
 * @param {string} scopeName - CSS scope class ('' for global)
 * @param {Object} palette - From derivePalette()
 * @param {Object} layout - From resolveLayout()
 * @returns {Object} CSS rules object
 */
export function generateThemedCSS(scopeName, palette, layout) {
  return Object.assign({},
    generateResetThemed(scopeName, palette),
    generateTypographyThemed(scopeName, palette, layout),
    generateButtons(scopeName, palette, layout),
    generateAlerts(scopeName, palette, layout),
    generateCards(scopeName, palette, layout),
    generateForms(scopeName, palette, layout),
    generateNavigation(scopeName, palette, layout),
    generateTables(scopeName, palette, layout),
    generateTabs(scopeName, palette, layout),
    generateListGroups(scopeName, palette, layout),
    generatePagination(scopeName, palette, layout),
    generateProgress(scopeName, palette, layout),
    generateProseThemed(scopeName, palette),
    generateBreadcrumbThemed(scopeName, palette, layout),
    generateCloseButtonThemed(scopeName, palette),
    generateSectionsThemed(scopeName, palette),
    generateAccordionThemed(scopeName, palette, layout),
    generateCarouselThemed(scopeName, palette),
    generateModalThemed(scopeName, palette, layout),
    generateToastThemed(scopeName, palette, layout),
    generateDropdownThemed(scopeName, palette, layout),
    generateSwitchThemed(scopeName, palette),
    generateSkeletonThemed(scopeName, palette),
    generateStatCardThemed(scopeName, palette, layout),
    generateTimelineThemed(scopeName, palette),
    generateStepperThemed(scopeName, palette),
    generateChipInputThemed(scopeName, palette, layout),
    generateFileUploadThemed(scopeName, palette, layout),
    generateRangeThemed(scopeName, palette),
    generateSearchThemed(scopeName, palette, layout),
    generateTooltipThemed(scopeName, palette, layout),
    generatePopoverThemed(scopeName, palette, layout),
    generateCodeDemoThemed(scopeName, palette, layout),
    generateNavPillsThemed(scopeName, palette, layout),
    generatePaletteClasses(scopeName, palette)
  );
}

// =========================================================================
// structuralRules — static CSS that never changes regardless of theme
// =========================================================================
//
// Architecture (v2.0.15 refactor):
//
//   structuralRules = { category: { selector: { prop: value } } }
//     Pure data — display, flex, position, overflow, cursor, z-index, etc.
//     NEVER contains colors, backgrounds, shadows, or border-colors.
//
//   generate*Themed(scope, palette, layout)
//     Computes all configurable CSS: colors from palette, padding from
//     spacing presets, border-radius from radius presets, box-shadow from
//     elevation presets, transitions from motion presets.
//
//   getStructuralStyles()  → flattens structuralRules + underscore aliases
//   getAllStyles()          → merge(getStructuralCSS(), generateThemedCSS())
//   defaultStyles           → backward-compat categorized view
//
// Adding a new BCCL component = add a category to structuralRules +
// a generate*Themed() function. That's it.
// =========================================================================

// Generate 12-column grid rules for a given class prefix (e.g. 'bw_col', 'bw_col_sm')
function _gridCols(prefix) {
  var r = {};
  for (var i = 1; i <= 12; i++) {
    var pct = +(100 * i / 12).toFixed(6) + '%';
    r['.' + prefix + '_' + i] = { 'flex': '0 0 ' + pct, 'max-width': pct };
  }
  return r;
}

var structuralRules = {
  // ---- Reset ----
  base: {
    '*': { 'box-sizing': 'border-box', 'margin': '0', 'padding': '0' },

    // The reset above zeroes padding on everything, which leaves a list with
    // list-style-position: outside rendering its markers to the LEFT of its own
    // content box -- overlapping whatever contains it. Put the indent back here
    // rather than in a class, so the reset replaces what it destroys instead of
    // leaving the damage for an opt-in to repair.
    //
    // Why 1.5em and not 1em. Measured in Chromium at a 16px root, marker ink
    // starts this far inside the list's own left edge:
    //
    //   1em -> 0px    1.25em -> 3px    1.5em -> 7px    2em -> 15px
    //
    // 1em looks like it should align the marker with the body-text edge, and
    // arithmetically it does -- but it aligns marker ink to the text BOX edge,
    // while the eye compares ink to ink. Every glyph has a left side bearing,
    // so at 1em the bullet reads as hanging outside the heading above it. That
    // is a real report from example 1a, not a theoretical concern. 1.5em clears
    // it with room to spare and matches what .bw_list has always shipped.
    //
    // em rather than rem so nested and smaller-type lists scale with their own
    // text instead of the root.
    // <menu> is a list too -- same list-style-position: outside, same markers
    // rendering to the left of a zeroed padding box. It is rare enough that
    // nobody reported it, which is exactly why it belongs here rather than in
    // whatever example trips over it next.
    'ul, ol, menu': { 'padding-left': '1.5em', 'margin-bottom': '1rem' },
    'ul ul, ul ol, ol ol, ol ul': { 'margin-bottom': '0' },

    // The same repair for the other two things `* { margin: 0 }` flattens.
    // A UA gives blockquote margin: 1em 40px and dd margin-left: 40px; the
    // reset takes both away, so a bare <blockquote> sits flush against body
    // text with nothing to say it is a quote, and a <dl> collapses into an
    // unreadable ladder of terms and definitions at the same indent.
    //
    // This is 2.1.6's rule applied where it was missed: plain markup should
    // look right with no class on it, and a class asks for something other
    // than the default rather than repairing one. .bw_quote is the former --
    // a rule with a coloured left border is styling you opt into, not the
    // repair for damage done here. Left indent only; the UA's symmetric 40px
    // right margin is a typographic opinion, not something the reset broke.
    // dl's half of the repair is just "a block of prose needs a bottom
    // margin", which is what the p rule in typography already says -- so it
    // says it there, for both, rather than as a second rule here.
    'blockquote': { 'margin': '0 0 1rem 1.5em' },
    'dd': { 'margin-left': '1.5em' },

    'html': {
      'font-size': '16px', 'line-height': '1.5',
      '-webkit-text-size-adjust': '100%',
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale'
    },
    'body': {
      'font-family': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      'font-size': '1rem', 'font-weight': '400', 'line-height': '1.6',
      'margin': '0', 'padding': '0',
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale'
    },
    '.bw_page': { 'min-height': '100vh', 'display': 'flex', 'flex-direction': 'column' },
    '.bw_page_content': { 'flex': '1', 'padding': '2rem 0' },
    'main': { 'display': 'block' },
    // border: 0 is a normalize-style reset that expects a border added back.
    // Without the two longhands below, a bare <hr> is 1px tall with no border
    // and no background -- it occupies space and paints nothing. The longhands
    // follow the shorthand so they win; colour comes from generateProseThemed.
    'hr': {
      'box-sizing': 'content-box', 'height': '0', 'overflow': 'visible', 'margin': '1rem 0',
      'border': '0', 'border-top-width': '1px', 'border-top-style': 'solid'
    },
    'hr:not([size])': { 'height': '1px' }
  },

  // ---- Typography ----
  typography: {
    'h1, h2, h3, h4, h5, h6': {
      'margin-top': '0', 'margin-bottom': '.5rem', 'font-weight': '600',
      'line-height': '1.25', 'letter-spacing': '-0.01em'
    },
    'h1': { 'font-size': 'calc(1.375rem + 1.5vw)' },
    'h2': { 'font-size': 'calc(1.325rem + .9vw)' },
    'h3': { 'font-size': 'calc(1.3rem + .6vw)' },
    'h4': { 'font-size': 'calc(1.275rem + .3vw)' },
    'h5': { 'font-size': '1.25rem' },
    'h6': { 'font-size': '1rem' },
    'p, dl': { 'margin-top': '0', 'margin-bottom': '1rem' },

    // ---- Prose elements ----
    // ul/ol and hr are repaired in structuralRules.base, where the reset that
    // broke them lives -- these two classes are kept for compatibility and now
    // just restate the default. .bw_quote and .bw_code are different: bare
    // blockquote gets its indent back in base like any other reset casualty,
    // and .bw_quote is the styling (border, colour) you opt into on top. .bw_code
    // the reset never touched at all -- it simply has no opinion, so it stays
    // opt-in.
    '.bw_list': { 'padding-left': '1.5em', 'margin-bottom': '1rem' },
    '.bw_list .bw_list': { 'margin-bottom': '0' },
    '.bw_hr': {
      'height': '0', 'margin': '1.5rem 0',
      'border': '0', 'border-top-width': '1px', 'border-top-style': 'solid'
    },
    '.bw_quote': {
      'margin': '0 0 1rem', 'padding': '0.5rem 0 0.5rem 1rem',
      'border-left-width': '3px', 'border-left-style': 'solid'
    },
    '.bw_code': {
      'font-family': 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      'font-size': '0.875em', 'padding': '0.15em 0.4em', 'border-radius': '4px'
    },
    'small': { 'font-size': '0.875rem' },
    'a': { 'text-decoration': 'none' },
    '.bw_display_4': { 'font-size': 'calc(1.475rem + 2.7vw)', 'font-weight': '300', 'line-height': '1.2' },
    '.bw_lead': { 'font-size': '1.25rem', 'font-weight': '300' },
    '.bw_h5': { 'font-size': '1.25rem' },
    '.bw_h6': { 'font-size': '1rem' }
  },

  // ---- Grid ----
  grid: {
    '.bw_bccl_container, .bw_container': {
      'width': '100%', 'padding-right': '0.75rem', 'padding-left': '0.75rem',
      'margin-right': 'auto', 'margin-left': 'auto'
    },
    '@media (min-width: 576px)': { '.bw_bccl_container, .bw_container': { 'max-width': '540px' } },
    '@media (min-width: 768px)': { '.bw_bccl_container, .bw_container': { 'max-width': '720px' } },
    '@media (min-width: 992px)': { '.bw_bccl_container, .bw_container': { 'max-width': '960px' } },
    '@media (min-width: 1200px)': { '.bw_bccl_container, .bw_container': { 'max-width': '1140px' } },
    '.bw_bccl_container_fluid': {
      'width': '100%', 'padding-right': '0.75rem', 'padding-left': '0.75rem',
      'margin-right': 'auto', 'margin-left': 'auto'
    },
    '.bw_row': {
      'display': 'flex', 'flex-wrap': 'wrap',
      'margin-right': '-0.375rem', 'margin-left': '-0.375rem'
    },
    '.bw_col, [class*="bw_col_"]': {
      'position': 'relative', 'width': '100%',
      'padding-right': '0.375rem', 'padding-left': '0.375rem'
    },
    '.bw_col': { 'flex-basis': '0', 'flex-grow': '1', 'max-width': '100%' },
    ..._gridCols('bw_col')
  },

  // ---- Buttons ----
  buttons: {
    '.bw_bccl_btn': {
      'display': 'inline-flex', 'align-items': 'center', 'justify-content': 'center',
      'font-weight': '500', 'line-height': '1.5', 'text-align': 'center',
      'text-decoration': 'none', 'vertical-align': 'middle', 'cursor': 'pointer',
      'user-select': 'none', 'border': '1px solid transparent',
      'font-size': '0.875rem', 'font-family': 'inherit', 'gap': '0.5rem'
    },
    '.bw_bccl_btn:hover': { 'text-decoration': 'none', 'transform': 'translateY(-1px)' },
    '.bw_bccl_btn:active': { 'transform': 'translateY(0)' },
    '.bw_bccl_btn:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '2px' },
    '.bw_bccl_btn:disabled': { 'opacity': '0.5', 'cursor': 'not-allowed', 'pointer-events': 'none' },
    '.bw_bccl_btn_block': { 'display': 'block', 'width': '100%' }
  },

  // ---- Cards ----
  cards: {
    '.bw_bccl_card': {
      'position': 'relative', 'display': 'flex', 'flex-direction': 'column',
      'min-width': '0', 'height': '100%', 'word-wrap': 'break-word',
      'background-clip': 'border-box', 'margin-bottom': '1.5rem', 'overflow': 'hidden'
    },
    '.bw_bccl_card_body': { 'flex': '1 1 auto' },
    '.bw_bccl_card_body > *:last-child': { 'margin-bottom': '0' },
    '.bw_bccl_card_title': { 'margin-bottom': '0.5rem', 'font-size': '1.125rem', 'font-weight': '600', 'line-height': '1.3' },
    '.bw_bccl_card_text': { 'margin-bottom': '0', 'font-size': '0.9375rem', 'line-height': '1.6' },
    '.bw_bccl_card_header': { 'margin-bottom': '0', 'font-weight': '600', 'font-size': '0.875rem' },
    '.bw_bccl_card_footer': { 'font-size': '0.875rem' },
    '.bw_bccl_card_hoverable': {},
    '.bw_bccl_card_hoverable:hover': { 'transform': 'translateY(-4px)' },
    '.bw_bccl_card_img_top': { 'width': '100%' },
    '.bw_bccl_card_img_bottom': { 'width': '100%' },
    '.bw_bccl_card_img_left': { 'width': '40%', 'object-fit': 'cover' },
    '.bw_bccl_card_img_right': { 'width': '40%', 'object-fit': 'cover' },
    '.bw_bccl_card_subtitle, .card-subtitle': { 'margin-top': '-0.25rem', 'margin-bottom': '0.5rem', 'font-size': '0.875rem' }
  },

  // ---- Forms ----
  forms: {
    '.bw_bccl_form_control': {
      'display': 'block', 'width': '100%',
      'font-size': '0.9375rem', 'font-weight': '400', 'line-height': '1.5',
      'background-clip': 'padding-box', 'appearance': 'none',
      'border': '1px solid transparent', 'font-family': 'inherit',
      'transition': 'border-color 0.15s ease-out, box-shadow 0.15s ease-out'
    },
    '.bw_bccl_form_control:focus': { 'outline': '2px solid currentColor', 'outline-offset': '-1px' },
    '.bw_bccl_form_control::placeholder': { 'opacity': '1' },
    '.bw_bccl_form_label': { 'display': 'block', 'margin-bottom': '0.375rem', 'font-size': '0.875rem', 'font-weight': '600' },
    '.bw_bccl_form_group': { 'margin-bottom': '1.25rem' },
    '.bw_bccl_form_text': { 'margin-top': '0.25rem', 'font-size': '0.8125rem' },
    'select.bw_bccl_form_control': {
      'padding-right': '2.25rem',
      'background-image': "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e\")",
      'background-repeat': 'no-repeat', 'background-position': 'right 0.75rem center',
      'background-size': '16px 12px'
    },
    'textarea.bw_bccl_form_control': { 'min-height': '5rem', 'resize': 'vertical' },
    '.bw_valid_feedback': { 'display': 'block', 'font-size': '0.875rem', 'margin-top': '0.25rem' },
    '.bw_invalid_feedback': { 'display': 'block', 'font-size': '0.875rem', 'margin-top': '0.25rem' }
  },

  // ---- Form checks ----
  formChecks: {
    '.bw_bccl_form_check': { 'display': 'flex', 'align-items': 'center', 'gap': '0.5rem', 'min-height': '1.5rem', 'margin-bottom': '0.25rem' },
    '.bw_bccl_form_check_input': { 'width': '1rem', 'height': '1rem', 'margin': '0', 'cursor': 'pointer', 'flex-shrink': '0', 'border-radius': '0.25rem', 'appearance': 'auto' },
    '.bw_bccl_form_check_input:disabled': { 'opacity': '0.5', 'cursor': 'not-allowed' },
    '.bw_bccl_form_check_label': { 'cursor': 'pointer', 'user-select': 'none', 'font-size': '0.9375rem' }
  },

  // ---- Navigation ----
  navigation: {
    '.bw_bccl_navbar': {
      'position': 'relative', 'display': 'flex', 'flex-wrap': 'wrap',
      'align-items': 'center', 'justify-content': 'space-between', 'padding': '0.5rem 1.5rem'
    },
    '.bw_bccl_navbar > .bw_bccl_container, .bw_bccl_navbar > .bw_container': { 'display': 'flex', 'flex-wrap': 'wrap', 'align-items': 'center', 'justify-content': 'space-between' },
    '.bw_bccl_navbar_brand': {
      'display': 'inline-flex', 'align-items': 'center', 'gap': '0.5rem',
      'padding-top': '0.25rem', 'padding-bottom': '0.25rem', 'margin-right': '1.5rem',
      'font-size': '1.125rem', 'font-weight': '600', 'line-height': 'inherit',
      'white-space': 'nowrap', 'text-decoration': 'none'
    },
    '.bw_bccl_navbar_nav': {
      'display': 'flex', 'flex-direction': 'row', 'padding-left': '0',
      'margin-bottom': '0', 'list-style': 'none', 'gap': '0.25rem'
    },
    '.bw_bccl_navbar_nav .bw_nav_link': {
      'display': 'block', 'text-decoration': 'none',
      'font-size': '0.875rem', 'font-weight': '500'
    }
  },

  // ---- Tables ----
  tables: {
    '.bw_bccl_table': {
      'width': '100%', 'margin-bottom': '1.5rem', 'vertical-align': 'top',
      'border-collapse': 'collapse', 'font-size': '0.9375rem', 'line-height': '1.5'
    },
    '.bw_bccl_table > :not(caption) > * > *': {
      'background-color': 'transparent',
      'border-bottom-width': '1px', 'border-bottom-style': 'solid'
    },
    '.bw_bccl_table > tbody': { 'vertical-align': 'inherit' },
    '.bw_bccl_table > thead': { 'vertical-align': 'bottom' },
    '.bw_bccl_table > thead > tr > *': {
      'font-size': '0.8125rem', 'font-weight': '600',
      'text-transform': 'uppercase', 'letter-spacing': '0.04em',
      'border-bottom-width': '2px'
    },
    '.bw_bccl_table caption': { 'font-size': '0.875rem', 'caption-side': 'bottom' },
    '.bw_bccl_table_bordered > :not(caption) > * > *': { 'border-width': '1px', 'border-style': 'solid' },
    '.bw_bccl_table_selectable > tbody > tr': { 'cursor': 'pointer' },
    '.bw_bccl_table > tbody > tr.bw_bccl_table_row_selected > *': { 'background-color': 'rgba(0, 102, 102, 0.1)' },
    '.bw_bccl_table_responsive': { 'overflow-x': 'auto', '-webkit-overflow-scrolling': 'touch' }
  },

  // ---- Alerts ----
  alerts: {
    '.bw_bccl_alert': {
      'position': 'relative', 'margin-bottom': '1rem',
      'border': '1px solid transparent',
      'font-size': '0.9375rem', 'line-height': '1.6'
    },
    '.bw_bccl_alert_heading, .alert-heading': { 'color': 'inherit' },
    '.bw_bccl_alert_link, .alert-link': { 'font-weight': '700' },
    '.bw_bccl_alert_dismissible': { 'padding-right': '3rem' },
    '.bw_bccl_alert_dismissible .btn-close': { 'position': 'absolute', 'top': '0', 'right': '0', 'z-index': '2', 'padding': '1.25rem 1rem' }
  },

  // ---- Badges ----
  badges: {
    '.bw_bccl_badge': {
      'display': 'inline-block', 'font-size': '0.875rem',
      'font-weight': '600', 'line-height': '1.3', 'text-align': 'center',
      'white-space': 'nowrap', 'vertical-align': 'baseline',
      'padding': '0.35rem 0.65rem', 'border-radius': '0.25rem'
    },
    '.bw_bccl_badge:empty': { 'display': 'none' },
    '.bw_bccl_badge_sm': { 'font-size': '0.75rem', 'padding': '0.25rem 0.5rem' },
    '.bw_bccl_badge_lg': { 'font-size': '1rem', 'padding': '0.5rem 0.875rem' },
    '.bw_bccl_badge_pill': { 'border-radius': '50rem' },
    '.btn .badge': { 'position': 'relative', 'top': '-1px' }
  },

  // ---- Progress ----
  progress: {
    '.bw_bccl_progress': { 'display': 'flex', 'height': '1.25rem', 'overflow': 'hidden', 'font-size': '.875rem' },
    '.bw_bccl_progress_bar': {
      'display': 'flex', 'flex-direction': 'column', 'justify-content': 'center',
      'overflow': 'hidden', 'text-align': 'center', 'white-space': 'nowrap', 'font-weight': '600'
    },
    '.bw_bccl_progress_bar_striped': {
      'background-image': 'linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)',
      'background-size': '1rem 1rem'
    },
    '.bw_bccl_progress_bar_animated': { 'animation': 'progress-bar-stripes 1s linear infinite' },
    '@keyframes progress-bar-stripes': { '0%': { 'background-position-x': '1rem' } }
  },

  // ---- Tabs ----
  tabs: {
    '.bw_nav': { 'display': 'flex', 'flex-wrap': 'wrap', 'padding-left': '0', 'margin-bottom': '0', 'list-style': 'none', 'gap': '0' },
    '.bw_nav_item': { 'display': 'block' },
    '.bw_nav_tabs .bw_nav_item': { 'margin-bottom': '-2px' },
    '.bw_nav_link': {
      'display': 'block', 'font-size': '0.875rem', 'font-weight': '500',
      'padding': '0.625rem 1rem',
      'text-decoration': 'none', 'cursor': 'pointer',
      'border': 'none', 'background': 'transparent', 'font-family': 'inherit'
    },
    '.bw_nav_link:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '-2px' },
    '.bw_nav_tabs .bw_nav_link': { 'border': 'none', 'border-bottom': '2px solid transparent', 'border-radius': '0', 'background-color': 'transparent' },
    '.bw_nav_vertical': { 'flex-direction': 'column' },
    '.bw_bccl_tab_content': { 'padding': '1.25rem 0' },
    '.bw_bccl_tab_pane': { 'display': 'none' },
    '.bw_bccl_tab_pane.active': { 'display': 'block' },
    '.bw_nav_scrollable': { 'flex-wrap': 'nowrap', 'overflow-x': 'auto', '-webkit-overflow-scrolling': 'touch', 'scrollbar-width': 'none' },
    '.bw_nav_scrollable::-webkit-scrollbar': { 'display': 'none' },
    '.bw_nav_scrollable .bw_nav_link': { 'white-space': 'nowrap' }
  },

  // ---- List groups ----
  listGroups: {
    '.bw_list_group': { 'display': 'flex', 'flex-direction': 'column', 'padding-left': '0', 'margin-bottom': '0' },
    '.bw_list_group_item': { 'position': 'relative', 'display': 'block', 'text-decoration': 'none', 'font-size': '0.9375rem' },
    '.bw_list_group_item:first-child': { 'border-top-left-radius': 'inherit', 'border-top-right-radius': 'inherit' },
    '.bw_list_group_item:last-child': { 'border-bottom-right-radius': 'inherit', 'border-bottom-left-radius': 'inherit' },
    '.bw_list_group_item + .bw_list_group_item': { 'border-top-width': '0' },
    '.bw_list_group_item.disabled': { 'pointer-events': 'none' },
    'a.bw_list_group_item': { 'cursor': 'pointer' },
    'a.bw_list_group_item:focus-visible, .bw_list_group_item:focus-visible': { 'z-index': '2', 'outline': '2px solid currentColor', 'outline-offset': '-2px' },
    '.bw_list_group_flush': { 'border-radius': '0' },
    '.bw_list_group_flush > .bw_list_group_item': { 'border-width': '0 0 1px', 'border-radius': '0' },
    '.bw_list_group_flush > .bw_list_group_item:last-child': { 'border-bottom-width': '0' }
  },

  // ---- Pagination ----
  pagination: {
    '.bw_bccl_pagination': { 'display': 'flex', 'padding-left': '0', 'list-style': 'none', 'margin-bottom': '0' },
    '.bw_page_item': { 'display': 'list-item', 'list-style': 'none' },
    '.bw_page_link': {
      'position': 'relative', 'display': 'block', 'padding': '0.375rem 0.75rem',
      'margin-left': '-1px', 'line-height': '1.25', 'text-decoration': 'none',
      'border': '1px solid transparent', 'cursor': 'pointer',
      'font-family': 'inherit', 'font-size': 'inherit', 'background': 'none'
    },
    '.bw_page_item:first-child .bw_page_link': { 'margin-left': '0' },
    // makePagination({ size }) -- same steps as the button sizes
    '.bw_bccl_pagination_sm .bw_page_link': { 'padding': '0.25rem 0.5rem', 'font-size': '0.8125rem' },
    '.bw_bccl_pagination_lg .bw_page_link': { 'padding': '0.625rem 1.25rem', 'font-size': '1.125rem' },
    '.bw_page_link:focus-visible': { 'z-index': '3', 'outline': '2px solid currentColor', 'outline-offset': '-2px' }
  },

  // ---- Breadcrumb ----
  breadcrumb: {
    '.bw_bccl_breadcrumb': { 'display': 'flex', 'flex-wrap': 'wrap', 'padding': '0 0', 'margin-bottom': '1rem', 'list-style': 'none' },
    // The <ol> inside the <nav>. Without this it renders as a numbered list,
    // because the flex/list-style rules above land on the wrapping <nav>.
    '.bw_bccl_breadcrumb_list': {
      'display': 'flex', 'flex-wrap': 'wrap', 'list-style': 'none',
      'margin': '0', 'padding': '0'
    },
    '.bw_bccl_breadcrumb_item': { 'display': 'flex' },
    '.bw_bccl_breadcrumb_item + .bw_bccl_breadcrumb_item': { 'padding-left': '0.5rem' },
    '.bw_bccl_breadcrumb_item + .bw_bccl_breadcrumb_item::before': { 'float': 'left', 'padding-right': '0.5rem', 'content': '"/"' },
    '.bw_bccl_breadcrumb_item a': { 'text-decoration': 'none' },
    '.bw_bccl_breadcrumb_item.active': { 'font-weight': '500' }
  },

  // ---- Hero ----
  hero: {
    '.bw_bccl_hero': { 'position': 'relative', 'overflow': 'hidden' },
    '.bw_bccl_hero_overlay': { 'position': 'absolute', 'top': '0', 'left': '0', 'right': '0', 'bottom': '0', 'z-index': '1' },
    '.bw_bccl_hero_content': { 'position': 'relative', 'z-index': '2' },
    '.bw_bccl_hero_title': { 'font-weight': '300', 'letter-spacing': '-0.05rem', 'color': 'inherit' },
    '.bw_bccl_hero_subtitle': { 'color': 'inherit' },
    '.bw_bccl_hero_actions': { 'display': 'flex', 'gap': '1rem', 'justify-content': 'center', 'flex-wrap': 'wrap' }
  },

  // ---- Features ----
  features: {
    '.bw_feature': { 'padding': '1rem' },
    '.bw_feature_icon': { 'display': 'inline-block', 'margin-bottom': '1rem' },
    '.bw_feature_title': { 'margin-bottom': '0.5rem' },
    '.bw_feature_grid': { 'width': '100%' }
  },

  // ---- Sections ----
  sections: {
    '.bw_section': { 'position': 'relative' },
    '.bw_section_header': { 'margin-bottom': '3rem' },
    '.bw_section_title': { 'margin-bottom': '1rem', 'font-weight': '300', 'font-size': 'calc(1.325rem + .9vw)' }
  },

  // ---- CTA ----
  cta: {
    '.bw_cta': { 'position': 'relative' },
    '.bw_cta_content': { 'max-width': '48rem', 'margin': '0 auto' },
    '.bw_cta_title': { 'font-weight': '300' },
    '.bw_cta_actions': { 'display': 'flex', 'gap': '1rem', 'justify-content': 'center', 'flex-wrap': 'wrap' }
  },

  // ---- Spinner ----
  spinner: {
    '.bw_spinner_border': {
      'display': 'inline-block', 'width': '2rem', 'height': '2rem',
      'vertical-align': '-0.125em', 'border': '0.25em solid currentcolor',
      'border-right-color': 'transparent', 'border-radius': '50%',
      'animation': 'bw_spinner_border 0.75s linear infinite'
    },
    '.bw_spinner_border_sm': { 'width': '1rem', 'height': '1rem', 'border-width': '0.2em' },
    '.bw_spinner_border_lg': { 'width': '3rem', 'height': '3rem', 'border-width': '0.3em' },
    '.bw_spinner_border_md': { 'width': '2rem', 'height': '2rem', 'border-width': '0.25em' },
    '.bw_spinner_grow': {
      'display': 'inline-block', 'width': '2rem', 'height': '2rem',
      'vertical-align': '-0.125em', 'border-radius': '50%', 'opacity': '0',
      'animation': 'bw_spinner_grow 0.75s linear infinite'
    },
    '.bw_spinner_grow_sm': { 'width': '1rem', 'height': '1rem' },
    '.bw_spinner_grow_lg': { 'width': '3rem', 'height': '3rem' },
    '.bw_spinner_grow_md': {},
    '@keyframes bw_spinner_border': { '100%': { 'transform': 'rotate(360deg)' } },
    '@keyframes bw_spinner_grow': { '0%': { 'transform': 'scale(0)' }, '50%': { 'opacity': '1', 'transform': 'none' } },
    '.bw_visually_hidden': {
      'position': 'absolute', 'width': '1px', 'height': '1px', 'padding': '0',
      'margin': '-1px', 'overflow': 'hidden', 'clip': 'rect(0, 0, 0, 0)',
      'white-space': 'nowrap', 'border': '0'
    }
  },

  // ---- Close button ----
  closeButton: {
    '.bw_close': {
      'display': 'inline-flex', 'align-items': 'center', 'justify-content': 'center',
      'width': '1.5rem', 'height': '1.5rem', 'padding': '0',
      'font-size': '1.25rem', 'font-weight': '700', 'line-height': '1',
      'background': 'transparent', 'border': '0', 'border-radius': '0.25rem', 'cursor': 'pointer',
      'transition': 'opacity 0.15s ease-out'
    },
    '.bw_close:hover': { 'opacity': '0.75' },
    '.bw_close:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '2px' }
  },

  // ---- Stacks ----
  stacks: {
    '.bw_vstack': { 'display': 'flex', 'flex-direction': 'column' },
    '.bw_hstack': { 'display': 'flex', 'flex-direction': 'row', 'align-items': 'center' },
    '.bw_gap_0': { 'gap': '0' },
    '.bw_gap_1': { 'gap': '0.25rem' },
    '.bw_gap_2': { 'gap': '0.5rem' },
    '.bw_gap_3': { 'gap': '1rem' },
    '.bw_gap_4': { 'gap': '1.5rem' },
    '.bw_gap_5': { 'gap': '3rem' }
  },

  // ---- Offsets ----
  offsets: {
    '.bw_offset_1': { 'margin-left': '8.333333%' },
    '.bw_offset_2': { 'margin-left': '16.666667%' },
    '.bw_offset_3': { 'margin-left': '25%' },
    '.bw_offset_4': { 'margin-left': '33.333333%' },
    '.bw_offset_5': { 'margin-left': '41.666667%' },
    '.bw_offset_6': { 'margin-left': '50%' },
    '.bw_offset_7': { 'margin-left': '58.333333%' },
    '.bw_offset_8': { 'margin-left': '66.666667%' },
    '.bw_offset_9': { 'margin-left': '75%' },
    '.bw_offset_10': { 'margin-left': '83.333333%' },
    '.bw_offset_11': { 'margin-left': '91.666667%' }
  },

  // ---- Code demo ----
  codeDemo: {
    '.bw_code_demo': { 'margin-bottom': '2rem' },
    '.bw_code_pre': { 'margin': '0', 'border': 'none', 'overflow-x': 'auto', 'max-width': '100%' },
    '.bw_code_block': {
      'display': 'block', 'padding': '1.25rem',
      'font-family': '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
      'font-size': '0.8125rem', 'line-height': '1.6'
    },
    '.bw_code_copy_btn': {
      'position': 'absolute', 'top': '0.5rem', 'right': '0.5rem',
      'padding': '0.25rem 0.625rem', 'font-size': '0.6875rem',
      'cursor': 'pointer', 'font-family': 'inherit'
    },
    '.bw_copy_btn': {
      'position': 'absolute', 'top': '0.5rem', 'right': '0.5rem',
      'padding': '0.25rem 0.625rem', 'font-size': '0.6875rem',
      'cursor': 'pointer', 'font-family': 'inherit'
    }
  },

  // ---- Button group ----
  buttonGroup: {
    '.bw_bccl_btn_group, .bw_bccl_btn_group_vertical': { 'position': 'relative', 'display': 'inline-flex', 'vertical-align': 'middle' },
    '.bw_bccl_btn_group > .bw_bccl_btn, .bw_bccl_btn_group_vertical > .bw_bccl_btn': { 'position': 'relative', 'flex': '1 1 auto', 'border-radius': '0', 'margin-left': '-1px' },
    '.bw_bccl_btn_group > .bw_bccl_btn:first-child': { 'margin-left': '0' },
    '.bw_bccl_btn_group > .bw_bccl_btn:last-child': {},
    '.bw_bccl_btn_group_lg > .bw_bccl_btn': { 'padding': '0.625rem 1.5rem', 'font-size': '1rem' },
    '.bw_bccl_btn_group_sm > .bw_bccl_btn': { 'padding': '0.25rem 0.75rem', 'font-size': '0.8125rem' },
    '.bw_bccl_btn_group_vertical': { 'flex-direction': 'column', 'align-items': 'flex-start', 'justify-content': 'center' },
    '.bw_bccl_btn_group_vertical > .bw_bccl_btn': { 'width': '100%', 'margin-left': '0', 'margin-top': '-1px' },
    '.bw_bccl_btn_group_vertical > .bw_bccl_btn:first-child': { 'margin-top': '0' },
    '.bw_bccl_btn_group_vertical > .bw_bccl_btn:last-child': {}
  },

  // ---- Accordion ----
  accordion: {
    '.bw_bccl_accordion': { 'overflow': 'hidden' },
    '.bw_bccl_accordion_item': { 'border': '1px solid transparent' },
    '.bw_bccl_accordion_item + .bw_bccl_accordion_item': { 'border-top': '0' },
    '.bw_bccl_accordion_header': { 'margin': '0' },
    '.bw_bccl_accordion_button': {
      'position': 'relative', 'display': 'flex', 'align-items': 'center', 'width': '100%',
      'padding': '0.875rem 1.25rem',
      'font-size': '1rem', 'font-weight': '500', 'text-align': 'left',
      'background-color': 'transparent', 'border': '0', 'overflow-anchor': 'none', 'cursor': 'pointer',
      'font-family': 'inherit'
    },
    '.bw_bccl_accordion_button::after': {
      'flex-shrink': '0', 'width': '1.25rem', 'height': '1.25rem', 'margin-left': 'auto',
      'content': '""',
      'background-image': "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e\")",
      'background-repeat': 'no-repeat', 'background-size': '1.25rem',
      'transition': 'transform 0.2s ease-out'
    },
    '.bw_bccl_accordion_button:not(.bw_collapsed)::after': { 'transform': 'rotate(-180deg)' },
    '.bw_bccl_accordion_body': { 'padding': '1rem 1.25rem' },
    '.bw_bccl_accordion_collapse': { 'max-height': '0', 'overflow': 'hidden', 'transition': 'max-height 0.3s ease' },
    '.bw_bccl_accordion_collapse.bw_collapse_show': { 'max-height': 'none' }
  },

  // ---- Carousel ----
  carousel: {
    '.bw_bccl_carousel': { 'position': 'relative', 'overflow': 'hidden' },
    '.bw_bccl_carousel_track': { 'display': 'flex', 'height': '100%' },
    '.bw_bccl_carousel_slide': {
      'min-width': '100%', 'flex-shrink': '0', 'overflow': 'hidden',
      'position': 'relative', 'display': 'flex', 'align-items': 'center', 'justify-content': 'center'
    },
    '.bw_bccl_carousel_slide img': { 'width': '100%', 'height': '100%', 'object-fit': 'cover' },
    '.bw_bccl_carousel_caption': { 'position': 'absolute', 'bottom': '0', 'left': '0', 'right': '0', 'padding': '0.75rem 1rem', 'font-size': '0.875rem' },
    '.bw_bccl_carousel_control': {
      'position': 'absolute', 'top': '50%', 'transform': 'translateY(-50%)',
      'width': '40px', 'height': '40px', 'border': 'none', 'border-radius': '50%',
      'cursor': 'pointer', 'display': 'flex', 'align-items': 'center', 'justify-content': 'center',
      'z-index': '2', 'padding': '0'
    },
    '.bw_bccl_carousel_control img': { 'width': '20px', 'height': '20px', 'pointer-events': 'none' },
    '.bw_bccl_carousel_control:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '2px' },
    '.bw_bccl_carousel_control_prev': { 'left': '10px' },
    '.bw_bccl_carousel_control_next': { 'right': '10px' },
    '.bw_bccl_carousel_indicators': {
      'position': 'absolute', 'bottom': '12px', 'left': '50%', 'transform': 'translateX(-50%)',
      'display': 'flex', 'gap': '6px', 'z-index': '2'
    },
    '.bw_bccl_carousel_indicator': {
      'width': '10px', 'height': '10px', 'border-radius': '50%', 'border': '2px solid transparent',
      'padding': '0', 'cursor': 'pointer'
    },
    '.bw_bccl_carousel_indicator:hover': { 'opacity': '0.8' }
  },

  // ---- Modal ----
  modal: {
    '.bw_bccl_modal': {
      'display': 'flex', 'align-items': 'center', 'justify-content': 'center',
      'position': 'fixed', 'top': '0', 'left': '0', 'width': '100%', 'height': '100%',
      'z-index': '1050', 'overflow-x': 'hidden', 'overflow-y': 'auto',
      'opacity': '0', 'visibility': 'hidden', 'pointer-events': 'none',
      'transition': 'opacity 0.2s ease-out, visibility 0.2s ease-out'
    },
    '.bw_bccl_modal.bw_bccl_modal_show': { 'opacity': '1', 'visibility': 'visible', 'pointer-events': 'auto' },
    '.bw_bccl_modal_dialog': {
      'position': 'relative', 'width': 'calc(100% - 1rem)', 'max-width': '500px', 'margin': '1.75rem auto',
      'pointer-events': 'none', 'transform': 'translateY(-16px)',
      'transition': 'transform 0.2s ease-out'
    },
    '.bw_bccl_modal.bw_bccl_modal_show .bw_bccl_modal_dialog': { 'transform': 'translateY(0)' },
    '.bw_bccl_modal_sm': { 'max-width': '300px' },
    '.bw_bccl_modal_lg': { 'max-width': '800px' },
    '.bw_bccl_modal_xl': { 'max-width': '1140px' },
    '.bw_bccl_modal_content': {
      'position': 'relative', 'display': 'flex', 'flex-direction': 'column', 'pointer-events': 'auto',
      'background-clip': 'padding-box', 'border': '1px solid transparent', 'outline': '0'
    },
    '.bw_bccl_modal_header': { 'display': 'flex', 'align-items': 'center', 'justify-content': 'space-between', 'padding': '1rem 1.25rem', 'border-bottom': '1px solid transparent' },
    '.bw_bccl_modal_title': { 'margin': '0', 'font-size': '1.25rem', 'font-weight': '600', 'line-height': '1.3' },
    '.bw_bccl_modal_body': { 'position': 'relative', 'flex': '1 1 auto', 'padding': '1rem 1.25rem' },
    '.bw_bccl_modal_footer': { 'display': 'flex', 'flex-wrap': 'wrap', 'align-items': 'center', 'justify-content': 'flex-end', 'gap': '0.5rem', 'padding': '0.75rem 1.25rem', 'border-top': '1px solid transparent' }
  },

  // ---- Toast ----
  toast: {
    '.bw_bccl_toast_container': {
      'position': 'fixed', 'z-index': '1080', 'pointer-events': 'none',
      'display': 'flex', 'flex-direction': 'column', 'gap': '0.5rem', 'padding': '1rem'
    },
    '.bw_bccl_toast_container.bw_bccl_toast_top_right': { 'top': '0', 'right': '0' },
    '.bw_bccl_toast_container.bw_bccl_toast_top_left': { 'top': '0', 'left': '0' },
    '.bw_bccl_toast_container.bw_bccl_toast_bottom_right': { 'bottom': '0', 'right': '0' },
    '.bw_bccl_toast_container.bw_bccl_toast_bottom_left': { 'bottom': '0', 'left': '0' },
    '.bw_bccl_toast_container.bw_bccl_toast_top_center': { 'top': '0', 'left': '50%', 'transform': 'translateX(-50%)' },
    '.bw_bccl_toast_container.bw_bccl_toast_bottom_center': { 'bottom': '0', 'left': '50%', 'transform': 'translateX(-50%)' },
    '.bw_bccl_toast': {
      'pointer-events': 'auto', 'width': '350px', 'max-width': 'calc(100vw - 2rem)', 'background-clip': 'padding-box',
      'opacity': '0', 'transform': 'translateY(-8px)',
      'transition': 'opacity 0.2s ease-out, transform 0.2s ease-out'
    },
    '.bw_bccl_toast.bw_bccl_toast_show': { 'opacity': '1', 'transform': 'translateY(0)' },
    '.bw_bccl_toast.bw_bccl_toast_hiding': { 'opacity': '0', 'transform': 'translateY(-8px)' },
    '.bw_bccl_toast_header': { 'display': 'flex', 'align-items': 'center', 'justify-content': 'space-between', 'padding': '0.5rem 0.75rem', 'font-size': '0.875rem', 'border-bottom': '1px solid transparent' },
    '.bw_bccl_toast_body': { 'padding': '0.5rem 0.75rem', 'font-size': '0.9375rem' }
  },

  // ---- Dropdown ----
  dropdown: {
    '.bw_bccl_dropdown': { 'position': 'relative', 'display': 'inline-block' },
    '.bw_bccl_dropdown_toggle::after': {
      'display': 'inline-block', 'margin-left': '0.255em', 'vertical-align': '0.255em',
      'content': '""', 'border-top': '0.3em solid', 'border-right': '0.3em solid transparent',
      'border-bottom': '0', 'border-left': '0.3em solid transparent'
    },
    '.bw_bccl_dropdown_menu': {
      'position': 'absolute', 'top': '100%', 'left': '0', 'z-index': '1000', 'display': 'block',
      'min-width': '10rem', 'padding': '0.5rem 0', 'margin': '0.125rem 0 0',
      'background-clip': 'padding-box', 'border': '1px solid transparent',
      'opacity': '0', 'visibility': 'hidden', 'pointer-events': 'none',
      'transform': 'translateY(-4px)',
      'transition': 'opacity 0.15s ease-out, transform 0.15s ease-out, visibility 0.15s ease-out'
    },
    '.bw_bccl_dropdown_menu.bw_bccl_dropdown_show': { 'opacity': '1', 'visibility': 'visible', 'pointer-events': 'auto', 'transform': 'translateY(0)' },
    '.bw_bccl_dropdown_menu_end': { 'left': 'auto', 'right': '0' },
    '.bw_bccl_dropdown_item': {
      'display': 'block', 'width': '100%', 'padding': '0.4rem 1rem', 'clear': 'both',
      'font-weight': '400', 'text-align': 'inherit', 'text-decoration': 'none', 'white-space': 'nowrap',
      'background-color': 'transparent', 'border': '0', 'font-size': '0.9375rem', 'cursor': 'pointer'
    },
    '.bw_bccl_dropdown_item:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '-2px' },
    '.bw_bccl_dropdown_divider': { 'height': '0', 'margin': '0.5rem 0', 'overflow': 'hidden', 'opacity': '1' }
  },

  // ---- Form switch ----
  formSwitch: {
    '.bw_bccl_form_switch': { 'padding-left': '2.5em' },
    '.bw_bccl_form_switch .bw_switch_input': {
      'width': '2em', 'height': '1.125em', 'margin-left': '-2.5em', 'border-radius': '2em',
      'appearance': 'none',
      'background-image': "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba(255,255,255,1)'/%3e%3c/svg%3e\")",
      'background-position': 'left center', 'background-repeat': 'no-repeat',
      'background-size': 'contain', 'cursor': 'pointer',
      'transition': 'background-color 0.15s ease-out, background-position 0.15s ease-out, border-color 0.15s ease-out'
    },
    '.bw_bccl_form_switch .bw_switch_input:checked': { 'background-position': 'right center' },
    '.bw_bccl_form_switch .bw_switch_input:disabled': { 'opacity': '0.5', 'cursor': 'not-allowed' }
  },

  // ---- Skeleton ----
  skeleton: {
    '.bw_skeleton': { 'background-size': '400% 100%', 'animation': 'bw_skeleton_shimmer 1.4s ease infinite' },
    '.bw_skeleton_text': { 'height': '1em', 'margin-bottom': '0.5rem' },
    '.bw_skeleton_circle': { 'border-radius': '50%' },
    '.bw_skeleton_rect': {},
    '.bw_skeleton_group': { 'display': 'flex', 'flex-direction': 'column' },
    '@keyframes bw_skeleton_shimmer': { '0%': { 'background-position': '100% 50%' }, '100%': { 'background-position': '0 50%' } }
  },

  // ---- Avatar ----
  avatar: {
    '.bw_avatar': {
      'display': 'inline-flex', 'align-items': 'center', 'justify-content': 'center',
      'border-radius': '50%', 'overflow': 'hidden', 'font-weight': '600',
      'text-transform': 'uppercase', 'vertical-align': 'middle', 'object-fit': 'cover'
    },
    '.bw_avatar_sm': { 'width': '2rem', 'height': '2rem', 'font-size': '0.75rem' },
    '.bw_avatar_md': { 'width': '3rem', 'height': '3rem', 'font-size': '1rem' },
    '.bw_avatar_lg': { 'width': '4rem', 'height': '4rem', 'font-size': '1.25rem' },
    '.bw_avatar_xl': { 'width': '5rem', 'height': '5rem', 'font-size': '1.5rem' }
  },

  // ---- Stat card ----
  statCard: {
    '.bw_stat_card': {
      'padding': '1.25rem',
      'border-left': '4px solid transparent',
      'background-color': 'inherit'
    },
    '.bw_stat_card:hover': { 'transform': 'translateY(-1px)' },
    '.bw_stat_icon': { 'font-size': '1.5rem', 'margin-bottom': '0.5rem' },
    '.bw_stat_value': { 'font-size': '2rem', 'font-weight': '700', 'line-height': '1.2' },
    '.bw_stat_label': { 'font-size': '0.875rem', 'margin-top': '0.25rem' },
    '.bw_stat_change': { 'font-size': '0.875rem', 'font-weight': '500', 'margin-top': '0.5rem' }
  },

  // ---- Tooltip ----
  tooltip: {
    '.bw_bccl_tooltip_wrapper': { 'position': 'relative', 'display': 'inline-block' },
    '.bw_bccl_tooltip': {
      'position': 'absolute', 'z-index': '999',
      'font-size': '0.875rem', 'white-space': 'nowrap', 'max-width': 'min(300px, calc(100vw - 1rem))', 'pointer-events': 'none',
      'opacity': '0', 'visibility': 'hidden'
    },
    '.bw_bccl_tooltip.bw_bccl_tooltip_show': { 'opacity': '1', 'visibility': 'visible' },
    '.bw_bccl_tooltip_top': { 'bottom': '100%', 'left': '50%', 'transform': 'translateX(-50%) translateY(-4px)', 'margin-bottom': '4px' },
    '.bw_bccl_tooltip_top.bw_bccl_tooltip_show': { 'transform': 'translateX(-50%) translateY(0)' },
    '.bw_bccl_tooltip_bottom': { 'top': '100%', 'left': '50%', 'transform': 'translateX(-50%) translateY(4px)', 'margin-top': '4px' },
    '.bw_bccl_tooltip_bottom.bw_bccl_tooltip_show': { 'transform': 'translateX(-50%) translateY(0)' },
    '.bw_bccl_tooltip_left': { 'right': '100%', 'top': '50%', 'transform': 'translateY(-50%) translateX(-4px)', 'margin-right': '4px' },
    '.bw_bccl_tooltip_left.bw_bccl_tooltip_show': { 'transform': 'translateY(-50%) translateX(0)' },
    '.bw_bccl_tooltip_right': { 'left': '100%', 'top': '50%', 'transform': 'translateY(-50%) translateX(4px)', 'margin-left': '4px' },
    '.bw_bccl_tooltip_right.bw_bccl_tooltip_show': { 'transform': 'translateY(-50%) translateX(0)' }
  },

  // ---- Popover ----
  popover: {
    '.bw_popover_wrapper': { 'position': 'relative', 'display': 'inline-block' },
    '.bw_popover_trigger': { 'cursor': 'pointer' },
    '.bw_popover': {
      'position': 'absolute', 'z-index': '1000',
      'min-width': '200px', 'max-width': 'min(320px, calc(100vw - 2rem))',
      'pointer-events': 'none', 'opacity': '0', 'visibility': 'hidden'
    },
    '.bw_popover.bw_popover_show': { 'opacity': '1', 'visibility': 'visible', 'pointer-events': 'auto' },
    '.bw_popover_header': { 'font-weight': '600', 'font-size': '0.9375rem' },
    '.bw_popover_body': { 'font-size': '0.875rem', 'line-height': '1.5' },
    '.bw_popover_top': { 'bottom': '100%', 'left': '50%', 'transform': 'translateX(-50%) translateY(-8px)', 'margin-bottom': '8px' },
    '.bw_popover_top.bw_popover_show': { 'transform': 'translateX(-50%) translateY(0)' },
    '.bw_popover_bottom': { 'top': '100%', 'left': '50%', 'transform': 'translateX(-50%) translateY(8px)', 'margin-top': '8px' },
    '.bw_popover_bottom.bw_popover_show': { 'transform': 'translateX(-50%) translateY(0)' },
    '.bw_popover_left': { 'right': '100%', 'top': '50%', 'transform': 'translateY(-50%) translateX(-8px)', 'margin-right': '8px' },
    '.bw_popover_left.bw_popover_show': { 'transform': 'translateY(-50%) translateX(0)' },
    '.bw_popover_right': { 'left': '100%', 'top': '50%', 'transform': 'translateY(-50%) translateX(8px)', 'margin-left': '8px' },
    '.bw_popover_right.bw_popover_show': { 'transform': 'translateY(-50%) translateX(0)' }
  },

  // ---- Search input ----
  searchInput: {
    '.bw_search_input': { 'position': 'relative', 'display': 'flex', 'align-items': 'center' },
    '.bw_search_input .bw_search_field': { 'padding-right': '2.5rem' },
    '.bw_search_clear': {
      'position': 'absolute', 'right': '0.5rem',
      'display': 'flex', 'align-items': 'center', 'justify-content': 'center',
      'width': '1.5rem', 'height': '1.5rem',
      'border': 'none', 'background': 'none',
      'font-size': '1.25rem', 'cursor': 'pointer', 'padding': '0', 'border-radius': '50%'
    },
    '.bw_search_clear:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '2px' }
  },

  // ---- Range ----
  range: {
    '.bw_range_wrapper': { 'margin-bottom': '1rem' },
    '.bw_range_label': { 'display': 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '0.5rem', 'font-size': '0.875rem', 'font-weight': '500' },
    '.bw_range_value': { 'font-weight': '600' },
    '.bw_range': { 'width': '100%', 'height': '0.5rem', 'padding': '0', 'appearance': 'none', 'border': 'none', 'border-radius': '0.25rem', 'cursor': 'pointer', 'outline': 'none' },
    '.bw_range:focus': { 'outline': 'none' },
    '.bw_range::-webkit-slider-thumb': {
      'appearance': 'none', 'width': '1.25rem', 'height': '1.25rem', 'border-radius': '50%',
      'border-width': '2px', 'border-style': 'solid', 'cursor': 'pointer'
    },
    '.bw_range::-moz-range-thumb': {
      'width': '1.25rem', 'height': '1.25rem', 'border-radius': '50%',
      'border-width': '2px', 'border-style': 'solid', 'cursor': 'pointer'
    },
    '.bw_range::-webkit-slider-thumb:hover': { 'transform': 'scale(1.15)' },
    '.bw_range:disabled': { 'opacity': '0.5', 'cursor': 'not-allowed' }
  },

  // ---- Media object ----
  mediaObject: {
    '.bw_media': { 'display': 'flex', 'align-items': 'flex-start', 'gap': '1rem' },
    '.bw_media_reverse': { 'flex-direction': 'row-reverse' },
    '.bw_media_img': { 'border-radius': '50%', 'object-fit': 'cover', 'flex-shrink': '0' },
    '.bw_media_body': { 'flex': '1', 'min-width': '0' },
    '.bw_media_title': { 'margin': '0 0 0.25rem 0', 'font-size': '1rem', 'font-weight': '600', 'line-height': '1.3' }
  },

  // ---- File upload ----
  fileUpload: {
    '.bw_file_upload': {
      'display': 'flex', 'flex-direction': 'column', 'align-items': 'center', 'justify-content': 'center',
      'border': '2px dashed transparent', 'cursor': 'pointer', 'text-align': 'center', 'position': 'relative'
    },
    '.bw_file_upload_icon': { 'font-size': '2rem', 'margin-bottom': '0.5rem' },
    '.bw_file_upload_text': { 'font-size': '0.875rem' },
    '.bw_file_upload_input': {
      'position': 'absolute', 'width': '1px', 'height': '1px', 'padding': '0',
      'margin': '-1px', 'overflow': 'hidden', 'clip': 'rect(0,0,0,0)', 'border': '0'
    }
  },

  // ---- Timeline ----
  timeline: {
    '.bw_bccl_timeline': { 'position': 'relative', 'padding-left': '2rem' },
    '.bw_bccl_timeline::before': {
      'content': '""', 'position': 'absolute', 'left': '0.5rem', 'top': '0', 'bottom': '0',
      'width': '2px'
    },
    '.bw_bccl_timeline_item': { 'position': 'relative', 'padding-bottom': '1.5rem' },
    '.bw_bccl_timeline_item:last-child': { 'padding-bottom': '0' },
    '.bw_bccl_timeline_marker': { 'position': 'absolute', 'left': '-1.75rem', 'top': '0.25rem', 'width': '0.75rem', 'height': '0.75rem', 'border-radius': '50%' },
    '.bw_bccl_timeline_content': { 'padding-left': '0.5rem' },
    '.bw_bccl_timeline_date': { 'font-size': '0.75rem', 'margin-bottom': '0.25rem', 'font-weight': '500' },
    '.bw_bccl_timeline_title': { 'font-size': '1rem', 'font-weight': '600', 'margin': '0 0 0.25rem 0', 'line-height': '1.3' },
    '.bw_bccl_timeline_text': { 'font-size': '0.875rem', 'margin': '0', 'line-height': '1.5' }
  },

  // ---- Stepper ----
  stepper: {
    '.bw_stepper': { 'display': 'flex', 'gap': '0', 'counter-reset': 'step' },
    '.bw_step': { 'flex': '1', 'display': 'flex', 'flex-direction': 'column', 'align-items': 'center', 'text-align': 'center', 'position': 'relative' },
    '.bw_step + .bw_step::before': { 'content': '""', 'position': 'absolute', 'top': '1rem', 'left': '-50%', 'right': '50%', 'height': '2px' },
    '.bw_step_indicator': {
      'width': '2rem', 'height': '2rem', 'border-radius': '50%',
      'display': 'flex', 'align-items': 'center', 'justify-content': 'center',
      'font-size': '0.875rem', 'font-weight': '600', 'position': 'relative', 'z-index': '1'
    },
    '.bw_step_body': { 'margin-top': '0.5rem' },
    '.bw_step_label': { 'font-size': '0.875rem', 'font-weight': '500' },
    '.bw_step_description': { 'font-size': '0.75rem', 'margin-top': '0.125rem' }
  },

  // ---- Chip input ----
  chipInput: {
    '.bw_chip_input': {
      'display': 'flex', 'flex-wrap': 'wrap', 'align-items': 'center', 'gap': '0.375rem',
      'padding': '0.375rem 0.5rem', 'min-height': '2.5rem', 'cursor': 'text'
    },
    '.bw_chip': {
      'display': 'inline-flex', 'align-items': 'center', 'gap': '0.25rem',
      'padding': '0.125rem 0.5rem', 'border-radius': '1rem',
      'font-size': '0.8125rem', 'line-height': '1.5', 'white-space': 'nowrap'
    },
    '.bw_chip_remove': {
      'display': 'inline-flex', 'align-items': 'center', 'justify-content': 'center',
      'width': '1rem', 'height': '1rem', 'border': 'none', 'background': 'none',
      'font-size': '0.875rem', 'cursor': 'pointer', 'padding': '0', 'border-radius': '50%'
    },
    '.bw_chip_remove:focus-visible': { 'outline': '2px solid currentColor', 'outline-offset': '1px' },
    '.bw_chip_field': { 'flex': '1', 'min-width': '80px', 'border': 'none', 'outline': 'none', 'font-size': '0.875rem', 'padding': '0.125rem 0', 'background': 'transparent' }
  },

  // ---- Bar chart ----
  barChart: {
    '.bw_bar_chart_container': { 'padding': '1rem', 'border': '1px solid transparent' },
    '.bw_bar_chart': { 'display': 'flex', 'align-items': 'flex-end', 'gap': '6px', 'padding': '0 0.5rem' },
    '.bw_bar_group': { 'flex': '1', 'display': 'flex', 'flex-direction': 'column', 'align-items': 'center', 'height': '100%', 'justify-content': 'flex-end' },
    '.bw_bar': { 'width': '100%', 'border-radius': '3px 3px 0 0', 'min-height': '4px' },
    '.bw_bar:hover': { 'opacity': '0.85' },
    '.bw_bar_value': { 'font-size': '0.65rem', 'font-weight': '600', 'margin-bottom': '2px', 'text-align': 'center' },
    '.bw_bar_label': { 'font-size': '0.7rem', 'margin-top': '4px', 'text-align': 'center' },
    '.bw_bar_chart_title': { 'font-size': '1.1rem', 'font-weight': '600', 'margin': '0 0 0.75rem 0' }
  },

  // ---- Responsive ----
  responsive: {
    '@media (min-width: 576px)': _gridCols('bw_col_sm'),
    '@media (min-width: 768px)': _gridCols('bw_col_md'),
    '@media (min-width: 992px)': _gridCols('bw_col_lg'),
    '@media (min-width: 1200px)': _gridCols('bw_col_xl'),
    '@media (max-width: 575px)': {
      '.bw_bccl_card_img_left, .bw_bccl_card-img-left': { 'width': '100%' },
      '.bw_bccl_card_img_right, .bw_bccl_card-img-right': { 'width': '100%' },
      '.bw_bccl_hero, .bw_bccl_hero': { 'padding': '2rem 1rem' },
      '.bw_cta_actions, .bw_cta-actions': { 'flex-direction': 'column' },
      '.bw_hstack, .bw_hstack': { 'flex-direction': 'column' },
      '.bw_bccl_modal_dialog': { 'margin': '0.5rem auto' },
      '.bw_bccl_modal_lg': { 'max-width': 'calc(100% - 1rem)' },
      '.bw_bccl_modal_xl': { 'max-width': 'calc(100% - 1rem)' },
      '.bw_bccl_navbar': { 'padding': '0.5rem 0.75rem' },
      '.bw_bccl_navbar_brand': { 'margin-right': '0.5rem', 'font-size': '1rem' },
      '.bw_bccl_navbar_nav': { 'flex-wrap': 'wrap' },
      '.bw_bccl_tooltip': { 'white-space': 'normal' },
      '.bw_bccl_table': { 'display': 'block', 'overflow-x': 'auto', '-webkit-overflow-scrolling': 'touch' },
      '.bw_col, .bw_col_1, .bw_col_2, .bw_col_3, .bw_col_4, .bw_col_5, .bw_col_6, .bw_col_7, .bw_col_8, .bw_col_9, .bw_col_10, .bw_col_11, .bw_col_12': { 'flex': '0 0 100%', 'max-width': '100%' },
      '.bw_bccl_container': { 'padding-right': '0.5rem', 'padding-left': '0.5rem' },
      '.bw_bccl_container_fluid': { 'padding-right': '0.5rem', 'padding-left': '0.5rem' }
    }
  }
};

// =========================================================================
// Utility CSS — structural, generated programmatically
// =========================================================================

function generateUtilityRules() {
  var rules = {};

  // Spacing
  var spacingValues = { '0': '0', '1': '.25rem', '2': '.5rem', '3': '1rem', '4': '1.5rem', '5': '3rem' };
  for (var k in spacingValues) {
    var v = spacingValues[k];
    rules['.bw_m_' + k] = { 'margin': v + ' !important' };
    rules['.bw_mt_' + k] = { 'margin-top': v + ' !important' };
    rules['.bw_mb_' + k] = { 'margin-bottom': v + ' !important' };
    rules['.bw_ms_' + k] = { 'margin-left': v + ' !important' };
    rules['.bw_me_' + k] = { 'margin-right': v + ' !important' };
    rules['.bw_p_' + k] = { 'padding': v + ' !important' };
    rules['.bw_pt_' + k] = { 'padding-top': v + ' !important' };
    rules['.bw_pb_' + k] = { 'padding-bottom': v + ' !important' };
    rules['.bw_ps_' + k] = { 'padding-left': v + ' !important' };
    rules['.bw_pe_' + k] = { 'padding-right': v + ' !important' };
    // Gutter k: half on each side of every column, pulled back by the row
    var h = parseFloat(v) / 2 + 'rem';
    rules['.bw_g_' + k] = { 'margin-left': '-' + h, 'margin-right': '-' + h, 'row-gap': v };
    rules['.bw_g_' + k + ' > *'] = { 'padding-left': h, 'padding-right': h };
  }
  rules['.bw_m_auto'] = { 'margin': 'auto !important' };
  rules['.bw_py_3'] = { 'padding-top': '1rem !important', 'padding-bottom': '1rem !important' };
  rules['.bw_py_4'] = { 'padding-top': '1.5rem !important', 'padding-bottom': '1.5rem !important' };
  rules['.bw_py_5'] = { 'padding-top': '3rem !important', 'padding-bottom': '3rem !important' };
  rules['.bw_py_6'] = { 'padding-top': '4rem !important', 'padding-bottom': '4rem !important' };

  // Display
  rules['.bw_d_none'] = { 'display': 'none' };
  rules['.bw_d_block'] = { 'display': 'block' };
  rules['.bw_d_inline'] = { 'display': 'inline' };
  rules['.bw_d_inline_block'] = { 'display': 'inline-block' };
  rules['.bw_d_flex'] = { 'display': 'flex' };

  // Text alignment
  rules['.bw_text_left'] = { 'text-align': 'left' };
  rules['.bw_text_right'] = { 'text-align': 'right' };
  rules['.bw_text_center'] = { 'text-align': 'center' };
  rules['.bw_text_justify'] = { 'text-align': 'justify' };

  // Font weight
  rules['.bw_fw_bold'] = { 'font-weight': '700' };
  rules['.bw_fw_semibold'] = { 'font-weight': '600' };
  rules['.bw_fw_normal'] = { 'font-weight': '400' };
  rules['.bw_fw_light'] = { 'font-weight': '300' };

  // Font style
  rules['.bw_fst_italic'] = { 'font-style': 'italic' };
  rules['.bw_fst_normal'] = { 'font-style': 'normal' };

  // Text decoration
  rules['.bw_text_underline'] = { 'text-decoration': 'underline' };
  rules['.bw_text_line_through'] = { 'text-decoration': 'line-through' };
  rules['.bw_text_decoration_none'] = { 'text-decoration': 'none' };

  // Text transform
  rules['.bw_text_uppercase'] = { 'text-transform': 'uppercase' };
  rules['.bw_text_lowercase'] = { 'text-transform': 'lowercase' };
  rules['.bw_text_capitalize'] = { 'text-transform': 'capitalize' };

  // White-space
  rules['.bw_text_wrap'] = { 'white-space': 'normal' };
  rules['.bw_text_nowrap'] = { 'white-space': 'nowrap' };

  // Font size
  rules['.bw_fs_sm'] = { 'font-size': '0.875rem' };
  rules['.bw_fs_base'] = { 'font-size': '1rem' };
  rules['.bw_fs_lg'] = { 'font-size': '1.25rem' };
  rules['.bw_fs_xl'] = { 'font-size': '1.5rem' };

  // Flexbox
  var jc = { start: 'flex-start', end: 'flex-end', center: 'center', between: 'space-between', around: 'space-around' };
  for (var jk in jc) { rules['.bw_justify_content_' + jk] = { 'justify-content': jc[jk] }; }
  var ai = { start: 'flex-start', end: 'flex-end', center: 'center' };
  for (var ak in ai) { rules['.bw_align_items_' + ak] = { 'align-items': ai[ak] }; }

  // Borders
  rules['.bw_border'] = { 'border': '1px solid transparent !important' };
  rules['.bw_border_0'] = { 'border': '0 !important' };
  rules['.bw_border_top_0'] = { 'border-top': '0 !important' };
  rules['.bw_border_end_0'] = { 'border-right': '0 !important' };
  rules['.bw_border_bottom_0'] = { 'border-bottom': '0 !important' };
  rules['.bw_border_start_0'] = { 'border-left': '0 !important' };

  // Rounded
  rules['.bw_rounded'] = { 'border-radius': '.375rem !important' };
  rules['.bw_rounded_0'] = { 'border-radius': '0 !important' };
  rules['.bw_rounded_1'] = { 'border-radius': '.25rem !important' };
  rules['.bw_rounded_2'] = { 'border-radius': '.375rem !important' };
  rules['.bw_rounded_3'] = { 'border-radius': '.5rem !important' };
  rules['.bw_rounded_circle'] = { 'border-radius': '50% !important' };
  rules['.bw_rounded_pill'] = { 'border-radius': '50rem !important' };

  // Shadows
  rules['.bw_shadow'] = { 'box-shadow': '0 .5rem 1rem rgba(0,0,0,.15) !important' };
  rules['.bw_shadow_sm'] = { 'box-shadow': '0 .125rem .25rem rgba(0,0,0,.075) !important' };
  rules['.bw_shadow_lg'] = { 'box-shadow': '0 1rem 3rem rgba(0,0,0,.175) !important' };
  rules['.bw_shadow_none'] = { 'box-shadow': 'none !important' };

  // Width/Height
  ['25', '50', '75', '100'].forEach(function(n) {
    rules['.bw_w_' + n] = { 'width': n + '% !important' };
    rules['.bw_h_' + n] = { 'height': n + '% !important' };
  });
  rules['.bw_w_auto'] = { 'width': 'auto !important' };
  rules['.bw_h_auto'] = { 'height': 'auto !important' };
  rules['.bw_mw_100'] = { 'max-width': '100% !important' };
  rules['.bw_mh_100'] = { 'max-height': '100% !important' };

  // Positioning
  ['static', 'relative', 'absolute', 'fixed', 'sticky'].forEach(function(p) {
    rules['.bw_position_' + p] = { 'position': p + ' !important' };
  });
  rules['.bw_top_0'] = { 'top': '0 !important' };
  rules['.bw_top_50'] = { 'top': '50% !important' };
  rules['.bw_top_100'] = { 'top': '100% !important' };
  rules['.bw_bottom_0'] = { 'bottom': '0 !important' };
  rules['.bw_bottom_50'] = { 'bottom': '50% !important' };
  rules['.bw_bottom_100'] = { 'bottom': '100% !important' };
  rules['.bw_start_0'] = { 'left': '0 !important' };
  rules['.bw_start_50'] = { 'left': '50% !important' };
  rules['.bw_start_100'] = { 'left': '100% !important' };
  rules['.bw_end_0'] = { 'right': '0 !important' };
  rules['.bw_end_50'] = { 'right': '50% !important' };
  rules['.bw_end_100'] = { 'right': '100% !important' };
  rules['.bw_translate_middle'] = { 'transform': 'translate(-50%, -50%) !important' };

  // Overflow
  ['auto', 'hidden', 'visible', 'scroll'].forEach(function(o) {
    rules['.bw_overflow_' + o] = { 'overflow': o + ' !important' };
  });

  // Typography — bw_ prefixed utilities via loops
  var _imp = function(p, v) { var o = {}; o[p] = v + ' !important'; return o; };
  [['fs',{'xs':'0.75rem','sm':'0.875rem','base':'1rem','lg':'1.125rem','xl':'1.25rem','2xl':'1.5rem'},'font-size'],
   ['fw',{light:'300',normal:'400',medium:'500',semibold:'600',bold:'700'},'font-weight'],
   ['lh',{tight:'1.25',normal:'1.5',relaxed:'1.75'},'line-height']
  ].forEach(function(d) { for (var dk in d[1]) rules['.bw_'+d[0]+'_'+dk] = _imp(d[2], d[1][dk]); });

  // List utilities
  rules['.bw_list_unstyled'] = { 'padding-left': '0', 'list-style': 'none' };
  rules['.bw_list_inline'] = { 'padding-left': '0', 'list-style': 'none' };
  rules['.bw_list_inline_item'] = { 'display': 'inline-block' };
  rules['.bw_list_inline_item:not(:last-child)'] = { 'margin-right': '.5rem' };

  // Flex utilities
  rules['.bw_flex'] = { 'display': 'flex' };
  rules['.bw_flex_column'] = { 'flex-direction': 'column' };
  rules['.bw_flex_wrap'] = { 'flex-wrap': 'wrap' };
  rules['.bw_flex_center'] = { 'display': 'flex', 'align-items': 'center', 'justify-content': 'center' };
  for (var gk in spacingValues) rules['.bw_gap_' + gk] = { 'gap': spacingValues[gk] + ' !important' };

  // Visibility
  rules['.bw_visible'] = { 'visibility': 'visible !important' };
  rules['.bw_invisible'] = { 'visibility': 'hidden !important' };

  // User select
  ['all', 'auto', 'none'].forEach(function(u) {
    rules['.bw_user_select_' + u] = { 'user-select': u + ' !important' };
  });

  // Pointer events
  rules['.bw_pe_none'] = { 'pointer-events': 'none !important' };
  rules['.bw_pe_auto'] = { 'pointer-events': 'auto !important' };

  // Opacity
  rules['.bw_opacity_0'] = { 'opacity': '0 !important' };
  rules['.bw_opacity_25'] = { 'opacity': '.25 !important' };
  rules['.bw_opacity_50'] = { 'opacity': '.5 !important' };
  rules['.bw_opacity_75'] = { 'opacity': '.75 !important' };
  rules['.bw_opacity_100'] = { 'opacity': '1 !important' };

  return rules;
}

// =========================================================================
// Flatten structuralRules → flat CSS rules object
// =========================================================================

function getStructuralCSS() {
  var result = {};
  var keys = Object.keys(structuralRules);
  for (var i = 0; i < keys.length; i++) {
    var sec = structuralRules[keys[i]];
    for (var k in sec) {
      // A repeated @media key merges and moves to its later position, so
      // breakpoint grid rules land after the base .bw_col_N rules (#103).
      var prev = result[k];
      delete result[k];
      result[k] = prev && k.charAt(0) === '@' ? Object.assign({}, prev, sec[k]) : sec[k];
    }
  }
  Object.assign(result, generateUtilityRules());

  // Accessibility: reduce motion
  result['@media (prefers-reduced-motion: reduce)'] = {
    '*, *::before, *::after': {
      'animation-duration': '0.01ms !important',
      'animation-iteration-count': '1 !important',
      'transition-duration': '0.01ms !important',
      'scroll-behavior': 'auto !important'
    }
  };

  return result;
}

// =========================================================================
// getStructuralStyles — public API (backward compatible)
// =========================================================================

/**
 * Get all structural (theme-independent) CSS rules.
 * @returns {Object} CSS rules object
 */
export function getStructuralStyles() {
  return getStructuralCSS();
}

/**
 * Get CSS reset rules only (box-sizing, html/body font, reduced-motion).
 * Separate from themed/structural rules for independent injection.
 * @returns {Object} CSS rules object for the reset layer
 */
export function getResetStyles() {
  var rules = {};
  Object.assign(rules, structuralRules.base);
  // Include reduced-motion preference
  rules['@media (prefers-reduced-motion: reduce)'] = {
    '*, *::before, *::after': {
      'animation-duration': '0.01ms !important',
      'animation-iteration-count': '1 !important',
      'transition-duration': '0.01ms !important',
      'scroll-behavior': 'auto !important'
    }
  };
  return rules;
}

// =========================================================================
// defaultStyles — backward-compatible categorized view
// =========================================================================
//
// Tests import `defaultStyles` and check for category keys.
// We export structuralRules directly as defaultStyles — it already
// has all the required category keys. The 'utilities' category is
// generated from generateUtilityRules().
// =========================================================================

export var defaultStyles = Object.assign({}, structuralRules, {
  // Merge utility categories for backward compat
  reset: structuralRules.base,
  enhancedCards: structuralRules.cards,
  tableResponsive: { '.bw_bccl_table_responsive': { 'overflow-x': 'auto', '-webkit-overflow-scrolling': 'touch' } },
  utilities: generateUtilityRules()
});

// =========================================================================
// getAllStyles — backward compatible
// =========================================================================

export function getAllStyles() {
  return getStructuralCSS();
}

// =========================================================================
// Theme configuration object (deprecated — use makeStyles())
// =========================================================================

export let theme = {
  colors: {
    primary: '#006666',
    secondary: '#6c757d',
    success: '#198754',
    danger: '#dc3545',
    warning: '#b38600',
    info: '#0891b2',
    light: '#f8f9fa',
    dark: '#212529',
    white: '#fff',
    black: '#000'
  },
  breakpoints: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400
  },
  spacing: {
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '1rem',
    4: '1.5rem',
    5: '3rem'
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem'
    }
  },
};

/**
 * Generate alternate-palette CSS scoped under `.bw_theme_alt`.
 * Uses the same `generateThemedCSS()` pipeline as the primary palette —
 * both sides go through identical code paths.
 *
 * @param {string} name - Theme scope name (e.g. 'ocean'). '' for global.
 * @param {Object} altPalette - From derivePalette(deriveAlternateConfig(...))
 * @param {Object} layout - From resolveLayout()
 * @returns {Object} CSS rules object scoped under .bw_theme_alt (+ optional .name)
 */
export function generateAlternateCSS(name, altPalette, layout) {
  var rawRules = generateThemedCSS('', altPalette, layout);
  var altPrefix = name ? '.' + name + '.bw_theme_alt' : '.bw_theme_alt';
  var altRules = scopeRulesUnder(rawRules, altPrefix);

  altRules[altPrefix + ' body, :root' + altPrefix + ' body'] = {
    'color': altPalette.dark.base,
    'background-color': altPalette.light.base
  };

  return altRules;
}

/**
 * Prefix every selector in a rules object with a scope selector.
 * Handles @media/@keyframes blocks and comma-separated selectors.
 * @param {Object} rules - CSS rules object
 * @param {string} prefix - Scope prefix (e.g. '#my-dashboard', '.bw_theme_alt')
 * @param {boolean} [compound=false] - If true, use compound selector (no space)
 *   for the first segment: `#scope.bw_theme_alt .sel` vs `#scope .sel`
 * @returns {Object} New rules object with scoped selectors
 */
export function scopeRulesUnder(rules, prefix, _compound) {
  var scoped = {};
  for (var sel in rules) {
    if (!Object.prototype.hasOwnProperty.call(rules, sel)) continue;
    if (sel.charAt(0) === '@') {
      var innerBlock = rules[sel];
      // @keyframes — steps (0%, 100%, from, to) are NOT selectors; pass through
      if (/^@keyframes\s/.test(sel)) {
        scoped[sel] = innerBlock;
      } else {
        // @media — prefix inner selectors
        var scopedInner = {};
        for (var innerSel in innerBlock) {
          if (!Object.prototype.hasOwnProperty.call(innerBlock, innerSel)) continue;
          scopedInner[_prefixSelector(innerSel, prefix)] = innerBlock[innerSel];
        }
        scoped[sel] = scopedInner;
      }
    } else {
      scoped[_prefixSelector(sel, prefix)] = rules[sel];
    }
  }
  return scoped;
}

function _prefixSelector(sel, prefix) {
  var parts = sel.split(',');
  var result = [];
  for (var i = 0; i < parts.length; i++) {
    result.push(prefix + ' ' + parts[i].trim());
  }
  return result.join(', ');
}

export function deepMerge(target, source) {
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])
        && target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

export function updateTheme(overrides) {
  deepMerge(theme, overrides);
}
